/**
 * Mobile UI bootstrap — Neovaa Configurator (v49 1:1 port).
 *
 * Ports the validated phone design in _design_ref_mobile_2026-05-24
 * (d4-spatial-light.jsx primary, d5-bloc.jsx for the editorial header) to
 * vanilla TypeScript. The desktop bootstrap in `App.ts` stays untouched;
 * `main.ts` chooses between `bootstrapUI` (desktop rail) and this entry via
 * `shouldUseMobileUI(profile.isMobile)` (real mobile OR ?forcemobile=1).
 *
 * v49 step order (all-SQUARE tiles — Alex 2026-05-29 non-negotiable, supersedes
 * the old mockup round legs):
 *   01 · Shape       — silhouette tiles (sofaSVG) + see-more modal
 *   Size             — thin sub-row: 4 pills + dimensions readout
 *   02 · Material    — square texture-thumbnail tiles + see-more modal
 *   03 · Colorway    — square color tiles tinted over the material + see-more
 *   04 · Legs        — square finish tiles (NOT circles)
 * Plus: sticky 3D hero (re-parented #viewer) with Liquid Glass pill + fullscreen,
 * restructured sticky CTA, Bloc editorial header.
 *
 * Engine mapping (no invented API — all existing calls):
 *   Shape  → state.shapeId = pick; formeId = resolveForme(shapeId, sizeId) → rebuildAssembly
 *   Size   → state.sizeId = pick; formeId = resolveForme(shapeId, sizeId) → rebuildAssembly
 *            (Shape and Size are INDEPENDENT — picking one never mutates the other;
 *             the size's `scale` is applied to the assembly group in rebuildAssembly)
 *   Material → applyFabric(swatchFor(material, colorway))  [textureUrl swaps weave]
 *   Colorway → applyFabric(swatchFor(material, colorway))  [color = zero-byte tint]
 *   Legs   → applyLeg(leg)
 *
 * Two-level fabric (materials/colorways) is the source of truth when present;
 * the flat fabrics[] is the single-axis fallback (desktop/POC untouched).
 */

import type { Signal } from "@/state/Store";
import { computePrice, formatPrice, swatchFor, resolveForme } from "@/state/Config";
import { shopifyActive, hydrateLivePrice, buyShopify, broadcastState, startEmbedAutoHeight, type LivePrice } from "@/state/commerce";
import { isOptionAvailable, optionHasVisual, configHasOOS, type Axis } from "@/state/availability";
import { resolveBOM } from "@/state/bom";
import type { AvailMap, CatalogLine } from "@/state/catalog";

/** Latest whole-product stock map + a repaint hook, shared so the option tiles can
 *  grey out-of-stock options the moment the catalog responds. Module-scoped: one
 *  configurator instance per page. */
let liveAvail: AvailMap | null = null;
let liveResult: LivePrice | null = null;
let repaintOOS: (() => void) | null = null;
let drawerEl: HTMLElement | null = null;

/** Walk every option swatch in the configurator and grey + block the ones whose
 *  Shopify variant is out of stock (contextual to the current shape/fabric). The 3D
 *  is never switched to an OOS look because the click guard early-returns. */
function applyAvailability(root: HTMLElement, product: ProductConfig, state: ConfigState, avail: AvailMap | null): void {
  // Shopify stock, or (360-first product) a combination that has no spin/GLB yet.
  if (!shopifyActive(product) && !product.spin) return;
  for (const btn of root.querySelectorAll<HTMLButtonElement>(".mob-swatch")) {
    let axis: Axis | null = null;
    let optionId: string | undefined;
    if (btn.dataset.colorwayId) { axis = "colorway"; optionId = btn.dataset.colorwayId; }
    else if (btn.dataset.legId) { axis = "leg"; optionId = btn.dataset.legId; }
    else if (btn.dataset.materialId) { axis = "material"; optionId = btn.dataset.materialId; }
    if (!axis || !optionId) continue;
    const visual = optionHasVisual(axis, optionId, product, state);
    const ok = visual && isOptionAvailable(axis, optionId, product, state, avail);
    btn.classList.toggle("is-oos", !ok);
    if (!ok) {
      btn.setAttribute("aria-disabled", "true");
      btn.dataset.oos = "1";
      // "visual" = not rendered for this shape yet : no "Sold out" caption, a plain toast.
      btn.dataset.oosKind = visual ? "stock" : "visual";
      btn.dataset.oosMsg = visual ? "" : `${btn.getAttribute("aria-label") ?? "This option"} isn't available for this shape yet.`;
    } else {
      btn.removeAttribute("aria-disabled");
      delete btn.dataset.oos;
      delete btn.dataset.oosKind;
      delete btn.dataset.oosMsg;
    }
  }
}

/** A short, human build summary for the cart confirmation ("3 Seater · Large · Lux
 *  Limestone · Brass legs"). */
function configSummary(product: ProductConfig, state: ConfigState): string {
  const parts: string[] = [];
  const shape = product.shapes?.find((sh) => sh.id === state.shapeId);
  const forme = product.formes.find((f) => f.id === state.formeId);
  if (shape?.label) parts.push(shape.label);
  else if (forme?.label) parts.push(forme.label);
  const size = product.sizes?.find((s) => s.id === state.sizeId);
  if (size?.label) parts.push(size.label);
  if (product.materials) {
    const mat = product.materials.find((m) => m.id === state.materialId);
    const cw = mat?.colorways.find((c) => c.id === state.colorwayId);
    if (mat && cw) parts.push(`${mat.label} ${cw.label}`);
    else if (cw) parts.push(cw.label);
  } else {
    const fab = product.fabrics.find((f) => f.id === state.fabricId);
    if (fab?.label) parts.push(fab.label);
  }
  const leg = product.legs.find((g) => g.id === state.legId);
  if (leg?.label) parts.push(`${leg.label} legs`);
  return parts.join(" · ");
}

interface DrawerOpts {
  snapshot: string | null;
  summary: string;
  lines: CatalogLine[];
  total: number | null;
  currency: string;
  onCheckout: () => void;
}

function closeCartDrawer(): void {
  if (!drawerEl) return;
  const d = drawerEl;
  drawerEl = null;
  d.classList.remove("is-open");
  setTimeout(() => d.remove(), 300);
}

/** The add-to-cart confirmation: a slide-in drawer showing the CONFIGURED snapshot,
 *  the full piece list, and the live total. Persistent (not a fading toast) so the
 *  shopper can review exactly what they built before checkout (condition 7). */
function openCartDrawer(opts: DrawerOpts): void {
  closeCartDrawer();
  const overlay = el("div", "nv-drawer-overlay");
  const panel = el("div", "nv-drawer");

  const head = el("div", "nv-drawer__head");
  head.appendChild(el("span", "nv-drawer__title", "Added to cart"));
  const close = el("button", "nv-drawer__close");
  close.type = "button";
  close.setAttribute("aria-label", "Close");
  close.textContent = "✕";
  close.addEventListener("click", closeCartDrawer);
  head.appendChild(close);
  panel.appendChild(head);

  if (opts.snapshot) {
    const imgWrap = el("div", "nv-drawer__img");
    const img = document.createElement("img");
    img.src = opts.snapshot;
    img.alt = "Your configured product";
    imgWrap.appendChild(img);
    panel.appendChild(imgWrap);
  }

  if (opts.summary) panel.appendChild(el("div", "nv-drawer__summary", opts.summary));

  const list = el("div", "nv-drawer__lines");
  for (const ln of opts.lines) {
    const row = el("div", "nv-drawer__line");
    const name = ln.title || ln.pieceId;
    row.appendChild(el("span", "nv-drawer__line-name", ln.quantity > 1 ? `${name} × ${ln.quantity}` : name));
    row.appendChild(el("span", "nv-drawer__line-price", formatPrice(ln.price * ln.quantity, opts.currency)));
    list.appendChild(row);
  }
  panel.appendChild(list);

  const totalRow = el("div", "nv-drawer__total");
  totalRow.appendChild(el("span", undefined, "Total"));
  totalRow.appendChild(el("span", undefined, opts.total != null ? formatPrice(opts.total, opts.currency) : ""));
  panel.appendChild(totalRow);

  const actions = el("div", "nv-drawer__actions");
  const keep = el("button", "nv-drawer__keep");
  keep.type = "button";
  keep.textContent = "Keep configuring";
  keep.addEventListener("click", closeCartDrawer);
  const checkout = el("button", "nv-drawer__checkout");
  checkout.type = "button";
  checkout.textContent = "Checkout";
  checkout.addEventListener("click", () => opts.onCheckout());
  actions.append(keep, checkout);
  panel.appendChild(actions);

  overlay.appendChild(panel);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeCartDrawer();
  });
  document.body.appendChild(overlay);
  drawerEl = overlay;
  requestAnimationFrame(() => overlay.classList.add("is-open"));
}
import { createCheckout, goToCheckout, currentSlug } from "@/state/checkout";
import { pushURL } from "@/state/URLState";
import { showToast } from "./Toast";
import { trackEvent } from "@/analytics/Tracker";
import { formeIcon } from "./formeIcons";
import { sofaSVG } from "./sofaSVG";
import { materialBG, finishFromId } from "./materialBG";
import { createModalSheet, type ModalSheet } from "./ModalSheet";
import { createDimensionsOverlay, type DimensionsOverlayApi } from "./DimensionsOverlay";
import { createPartsOverlay, type PartsOverlayApi } from "./PartsOverlay";
import type { ArController } from "./ArController";
import type { Engine } from "@/engine/Engine";
import type {
  ConfigState,
  ProductConfig,
  ShapeOption,
  MaterialOption,
  ColorwayOption,
  LegOption,
  SwatchRowDef,
  SwatchRowOption,
} from "@/types";

export interface MobileBootOpts {
  product: ProductConfig;
  state: Signal<ConfigState>;
  engine: Engine;
  /** AR controller — generates the iOS USDZ from the live model and routes the
   *  "View in your space" tap. Created in main.ts where engine + product exist. */
  ar: ArController;
}

/**
 * Early mobile mount — runs BEFORE the heavy `await engine.rebuildAssembly(...)`
 * so the re-parented #viewer (and the cover freeze-frame inside it) is already in
 * the VISIBLE mobile hero during the cold GLB load, instead of stranded inside the
 * FOUC-hidden `.cfg` (which is `visibility:hidden` on mobile until the viewer moves
 * out). Builds ONLY the structural shell needed to make the viewer visible:
 *   - body[data-mobile="1"]  → flips the mobile CSS on (un-hides #mobile-cfg)
 *   - #mobile-cfg > .mob-config-section > .mob-hero > .mob-hero__stage
 *   - re-parents #viewer into .mob-hero__stage (move, not clone — live canvas)
 *   - injects the glass viewer overlay
 * It does NOT touch state, build tiles, or subscribe — that needs the assembly and
 * happens later in bootstrapMobileUI(), which REUSES this shell (no innerHTML wipe).
 * Idempotent: calling it again returns the existing root. Returns the section root
 * so bootstrapMobileUI can append the rest of the UI under it.
 */
export function mountMobileHero(opts?: ViewerOverlayOpts): HTMLElement | null {
  const host = document.getElementById("mobile-cfg") as HTMLElement | null;
  if (!host) {
    console.warn("[mobile] #mobile-cfg root not found; skipping early hero mount");
    return null;
  }
  document.body.dataset.mobile = "1";

  // Idempotent — if the shell already exists (e.g. real-mobile path called us
  // twice), reuse it instead of stacking a second section.
  const existing = host.querySelector<HTMLElement>(".mob-config-section");
  if (existing) return existing;

  const root = el("div", "mob-config-section");
  host.appendChild(root);

  // Sticky hero — re-parent the engine's #viewer node here (zero engine change).
  const heroWrap = el("div", "mob-hero");
  const heroStage = el("div", "mob-hero__stage");
  heroWrap.appendChild(heroStage);
  root.appendChild(heroWrap);

  const viewer = document.getElementById("viewer");
  if (viewer) {
    heroStage.appendChild(viewer); // move, not clone — keeps the live WebGL canvas
    injectViewerOverlay(opts);
  } else {
    console.warn("[mobile] #viewer not found; viewer hero will be empty");
  }

  return root;
}

interface LruWindows {
  forme: number[];
  material: number[];
  colorway: number[];
  leg: number[];
}

interface TileApi {
  root: HTMLElement;
  sync(): void;
}

/** Index of an item by id in a list, or 0 if not found. */
function idxOf<T extends { id: string }>(list: T[], id: string | undefined): number {
  if (id == null) return 0;
  const i = list.findIndex((x) => x.id === id);
  return i >= 0 ? i : 0;
}

/** Initial visible[] for a dimension — keep [0..3] unless selected is past that. */
function initialWindow(len: number, selected: number): number[] {
  if (len <= 4) return Array.from({ length: len }, (_, i) => i);
  if (selected < 4) return [0, 1, 2, 3];
  return [0, 1, 2, selected];
}

/** LRU FIFO push: if newIdx not present, drop [0] and append. */
function pushLru(win: number[], newIdx: number, total: number): number[] {
  if (total <= 4) return win;
  if (win.includes(newIdx)) return win;
  return [win[1], win[2], win[3], newIdx];
}

/** Active mobile theme, read from <html data-nv-theme> (set by main.ts). */
function currentTheme(): "spatial-dark" | "spatial-light" | "bloc" {
  const t = document.documentElement.dataset.nvTheme;
  if (t === "spatial-dark" || t === "bloc") return t;
  return "spatial-light";
}

/** Background for the ModalSheet. Prefer the live surface token (--nv-bg) — which the
 *  host-theme adaptation overrides to the merchant's background — so the see-more sheet
 *  matches the store; fall back to the per-theme surface. */
function themeSheetBg(): string {
  const v = typeof document !== "undefined" && document.body
    ? getComputedStyle(document.body).getPropertyValue("--nv-bg").trim()
    : "";
  if (v) return v;
  switch (currentTheme()) {
    case "spatial-dark":
      return "#0A0B10";
    case "bloc":
      return "#FFF8EE";
    default:
      return "#F5F1E8";
  }
}

/* ── Two-level fabric resolution (with single-axis fallback) ──────────────── */

interface FabricModel {
  /** Material lines. When the product has no `materials`, we synthesize a single
   *  line from fabrics[] so the two-axis UI still renders a Material + Colorway. */
  materials: MaterialOption[];
}

function resolveFabricModel(product: ProductConfig): FabricModel {
  if (product.materials && product.materials.length > 0) {
    return { materials: product.materials };
  }
  // Fallback: synthesize one "Fabric" material whose colorways are the flat list.
  const colorways: ColorwayOption[] = product.fabrics.map((f) => ({
    id: f.id,
    label: f.label,
    color: f.color ?? "#888888",
    priceDelta: f.priceDelta,
  }));
  return {
    materials: [
      {
        id: "mat-default",
        label: "Fabric",
        finish: "boucle",
        thumbUrl: "/mobile-textures/boucle.jpg",
        colorways,
      },
    ],
  };
}

/** The currently-selected material, defaulting to the first. */
function currentMaterial(model: FabricModel, s: ConfigState): MaterialOption {
  return model.materials.find((m) => m.id === s.materialId) ?? model.materials[0];
}

/** The currently-selected colorway within `mat`, defaulting to the first. */
function currentColorway(mat: MaterialOption, s: ConfigState): ColorwayOption {
  return mat.colorways.find((c) => c.id === s.colorwayId) ?? mat.colorways[0];
}

export function bootstrapMobileUI(opts: MobileBootOpts): void {
  const { product, state, engine, ar } = opts;
  document.body.dataset.mobile = "1";

  // Embed mode (?embed=1, set by the embed snippet's iframe): carve the configurator
  // OUT of its mock product page. The merchant's real PDP already provides the brand
  // header, the reviews and the related-products/copy, so we render ONLY our
  // deliverable — viewer + options + dynamic price + buy — and drop the mock chrome
  // (buildHeader, buildHostDemo). Standalone (no ?embed) still shows the full demo
  // page so we can preview the configurator in context.
  const params0 = new URLSearchParams(location.search);
  const embed = params0.has("embed");
  // Self-contained PDP mode (?embed=1&cta=self): the configurator IS the product page —
  // it carries its own name + live price + Add-to-cart in the right rail, replacing the
  // store's gallery + variant selector. Plain ?embed=1 (no cta=self) stays a viewer+options
  // block that DRIVES the host page's native price/cart instead.
  const pdp = embed && params0.get("cta") === "self";
  document.body.dataset.embed = embed ? "1" : "";
  if (pdp) document.body.dataset.pdp = "1";

  const host = document.getElementById("mobile-cfg") as HTMLElement | null;
  if (!host) {
    console.warn("[mobile] #mobile-cfg root not found; skipping mobile bootstrap");
    return;
  }

  // Reuse the early shell (mountMobileHero) if it ran before rebuildAssembly so the
  // cover showed during the load. Its hero + re-parented #viewer stay put; we only
  // append the header + steps + CTA around them — NEVER innerHTML="" (that would
  // re-orphan #viewer back into the hidden .cfg and kill the live canvas/cover).
  // If no shell exists yet (defensive: real-mobile path that skipped the early
  // mount), build one now.
  const root = mountMobileHero({ engine, ar }) ?? (() => {
    const r = el("div", "mob-config-section");
    host.appendChild(r);
    return r;
  })();
  const heroWrap = root.querySelector<HTMLElement>(".mob-hero");
  // If the early hero mount (main.ts) built the overlay BEFORE the engine/product
  // were available, its Dimensions + AR items are inert. Now wire both into the
  // existing overlay (idempotent, no rebuild).
  wireOverlayDimensions(engine, ar, product);

  const model = resolveFabricModel(product);
  const shapes = resolveShapeList(product);

  // Backfill state for the new axes so the UI + engine agree on a starting point.
  // Shape + Size are INDEPENDENT (v50): derive each from the initial formeId's
  // tags when present, else default to the first option. We never cross-set them.
  {
    const s = state.get();
    const patch: Partial<ConfigState> = {};
    const initialForme = product.formes.find((f) => f.id === s.formeId);
    if (!s.shapeId) {
      // From the initial forme's shapeId tag, else its own id (v4 compat), else first.
      patch.shapeId = initialForme?.shapeId ?? initialForme?.id ?? shapes[0]?.id;
    }
    if (product.sizes && product.sizes.length > 0 && !s.sizeId) {
      // From the initial forme's sizeId tag, else the first size.
      patch.sizeId = initialForme?.sizeId ?? product.sizes[0].id;
    }
    // Re-resolve the formeId from the (shape, size) pair so the engine and the
    // tiles agree from the very first paint (the initial formeId may not be the
    // grid cell for the defaulted shape+size).
    const resolvedFormeId = resolveForme(
      product,
      patch.shapeId ?? s.shapeId,
      patch.sizeId ?? s.sizeId,
    ).id;
    if (resolvedFormeId !== s.formeId) patch.formeId = resolvedFormeId;
    if (!s.materialId) patch.materialId = model.materials[0].id;
    const mat0 = model.materials.find((m) => m.id === (patch.materialId ?? s.materialId)) ?? model.materials[0];
    if (!s.colorwayId && mat0.colorways.length > 0) {
      // Align colorway with the current fabricId if it maps to a colorway in mat0.
      // (Guarded: a product with no fabrics — e.g. a lamp — has no colorways.)
      const byFabric = mat0.colorways.find((c) => c.id === s.fabricId);
      patch.colorwayId = (byFabric ?? mat0.colorways[0]).id;
      patch.fabricId = patch.colorwayId;
    }
    if (Object.keys(patch).length > 0) state.set({ ...s, ...patch });
    // No build here: bootstrapMobileUI runs before the initial load (main.ts),
    // and main.ts performs the single initial rebuildAssembly right after, using
    // this now-backfilled state — so the first build already targets the resolved
    // (shape × size) formeId. Triggering one here would double-build / race.
  }

  const startMat = currentMaterial(model, state.get());

  const lru: LruWindows = {
    forme: initialWindow(shapes.length, idxOf(shapes, state.get().shapeId)),
    material: initialWindow(model.materials.length, idxOf(model.materials, state.get().materialId)),
    colorway: initialWindow(startMat.colorways.length, idxOf(startMat.colorways, state.get().colorwayId)),
    leg: initialWindow(product.legs.length, idxOf(product.legs, state.get().legId)),
  };

  // ── Layout: header → sticky hero → steps (5 sections) → CTA → host demo.
  // The hero (+ re-parented #viewer) is already in `root` from mountMobileHero();
  // insert the header BEFORE it (original visual order: header → hero → steps),
  // and append steps/CTA/hostDemo AFTER it.
  if (!embed) {
    // A brand's identity lives most in its TOP CHROME (promo strip + nav bar with
    // logo, links, icons) — the most recognizable surface of any store (Alex
    // 2026-06-12: without it, brand colors alone don't read as "their site").
    // When the config carries chrome, render the full brand header above the
    // centered configurator; otherwise fall back to the lone masthead.
    const chromeHost = document.getElementById("mobile-cfg");
    if (product.theme?.chrome && chromeHost) {
      chromeHost.insertBefore(buildBrandChrome(product), root);
      document.body.dataset.nvChrome = "1";
    } else {
      const header = buildHeader(product);
      if (heroWrap) root.insertBefore(header, heroWrap);
      else root.appendChild(header);
    }
  }

  // Bounded sticky zone: the hero is sticky RELATIVE TO THIS ZONE (hero + option
  // steps), not the whole page. position:sticky releases at its containing block's
  // bottom, so wrapping hero + steps makes the pinned viewer let go exactly at the
  // price/CTA — scroll past the options and the viewer is pushed up and away, then
  // the page scrolls normally (no viewer stuck over everything). Moving the hero in
  // is a DOM move, not a clone, so the live #viewer canvas is preserved.
  const stickyZone = el("div", "mob-sticky-zone");
  if (heroWrap) {
    root.insertBefore(stickyZone, heroWrap);
    stickyZone.appendChild(heroWrap); // move hero out of root, into the zone
  } else {
    root.appendChild(stickyZone);
  }

  // PDP mode: product name + live price at the TOP of the right rail (next to the viewer),
  // above the options. The CTA's price hydration keeps this in sync (single source).
  // The rail head (product name + live price atop the options column) renders in
  // PDP embeds AND standalone: a real PDP opens its rail with the product title
  // and price (Alex 2026-06-12 — "missing the price and the sofa name"). On a
  // narrow standalone phone the CTA bar already carries name+price, so CSS hides
  // the head there and shows it from the 920px two-column layout up.
  const railHead = buildRailHead(product);
  if (!pdp) railHead.root.classList.add("mob-rail-head--standalone");
  stickyZone.appendChild(railHead.root);

  const steps = el("div", "mob-steps");
  stickyZone.appendChild(steps);

  const tiles: TileApi[] = [];
  const matCtx: FabricCtx = { product, state, engine, lru, host, model };

  // Config-driven row list (multi-tenant): the product declares which rows to show +
  // in what order via `product.rows`. A row is either a built-in axis NAME (reuses the
  // proven builder) or a generic declared OBJECT (e.g. a lamp's "Shade colour" recolor
  // row — no code per product). A missing axis / unknown kind is SKIPPED (graceful for
  // paying clients). Absent → the default sofa order (un-migrated configs unchanged).
  const DEFAULT_ROWS = ["shape", "size", "material", "colorway", "legs"];
  const rowEntries = product.rows && product.rows.length > 0 ? product.rows : DEFAULT_ROWS;
  for (const entry of rowEntries) {
    let tile: TileApi | null = null;
    if (typeof entry === "object") {
      if (entry.kind === "swatch" && entry.options && entry.options.length > 0) {
        tile = buildGenericSwatchRow(entry, { state, engine, host });
      }
    } else {
      switch (entry) {
        case "shape":
          // Shape axis is INDEPENDENT of Size: picking a shape keeps the current size.
          tile = buildShapeTile({
            product, shapes, state, engine, lru, host,
            onPick: (shapeId) => selectShape(shapeId, product, state, engine),
          });
          break;
        case "size":
          if (product.sizes && product.sizes.length > 0) {
            tile = buildSizeRow({
              product, state, engine,
              onPick: (sizeId) => selectSize(sizeId, product, state, engine),
            });
          }
          break;
        case "material":
          tile = buildMaterialTile(matCtx);
          break;
        case "colorway":
          tile = buildColorwayTile(matCtx);
          break;
        case "legs":
          if (product.legs && product.legs.length > 0) {
            tile = buildLegTile({
              product, state, engine, lru, host,
              onPick: (id) => selectLeg(id, product, state, engine),
            });
          }
          break;
        default:
          break; // unknown built-in kind → skip
      }
    }
    if (tile) tiles.push(tile);
  }

  for (const t of tiles) steps.appendChild(t.root);

  // PDP gallery strip under the hero (desktop ≥920px, CSS-gated): every brand
  // PDP anchors its hero with imagery below (Loaf thumbnails, Koala gallery,
  // Castlery features) — without it the viewer reads oversized and the column
  // dies into whitespace (Alex 2026-06-12). Config-owned renders only: the
  // CURRENT shape's CGI tile (live, follows the selection) + the active
  // material's fabric macro shots. Presentational — the 3D above IS the product.
  if (heroWrap && !heroWrap.querySelector(".mob-gallery")) {
    const gal = el("div", "mob-gallery");
    const shapeThumb = document.createElement("img");
    shapeThumb.className = "mob-gallery__img mob-gallery__img--shape";
    shapeThumb.alt = "";
    gal.appendChild(shapeThumb);
    for (const cw of (product.materials?.[0]?.colorways ?? []).slice(0, 4)) {
      if (!cw.image) continue;
      const img = document.createElement("img");
      img.className = "mob-gallery__img";
      img.src = cw.image;
      img.alt = cw.label;
      gal.appendChild(img);
    }
    heroWrap.appendChild(gal);
    const syncGallery = () => {
      const s = state.get();
      const shapeId = s.shapeId ?? product.formes.find((f) => f.id === s.formeId)?.shapeId;
      const sh = product.shapes?.find((x) => x.id === shapeId);
      if (sh?.image) {
        if (shapeThumb.src !== sh.image) shapeThumb.src = sh.image;
        shapeThumb.style.display = "";
      } else {
        shapeThumb.style.display = "none";
      }
    };
    syncGallery();
    tiles.push({ root: gal, sync: syncGallery }); // rides the existing sync loop
  }

  const cta = buildCta(product, state, engine, { pdp, headPriceEl: railHead?.priceEl, headInstallEl: railHead?.installEl });
  root.appendChild(cta.root);

  // Photo grid = the PDP gallery. 4 square tiles = 4 angles of the CURRENTLY selected
  // shape×colour, pulled straight from the SAME pre-rendered 360 flipbook frames (Alex
  // 2026-06-20: "few shots from the 360, per colour per shape"). 36 frames = 10°/frame →
  // front(0°) · 3/4(50°) · side(90°) · 3/4-other(310°). A colour/shape with no 360 rendered
  // yet → its frames 404 → that tile hides itself (never a broken image). Desktop ≥920px:
  // tiles fill the column under the sticky viewer; phone: below the CTA. Click → lightbox.
  if (["neovaa", "koala", "castlery", "casablanco"].includes(product.theme?.brandSkin ?? "") && !root.querySelector(".mob-photogrid")) {
    const photogrid = el("div", "mob-photogrid");
    // front · 3/4 · side · 3/4-other, relatifs a la frame de face du produit (spin.frontIndex)
    const F0 = product.spin?.frontIndex ?? 0, NF = product.spin?.count ?? 36;
    const FRAME_IDX = [0, 5, 9, -1].map((d) => ((F0 + d) % NF + NF) % NF);
    const tileEls: HTMLElement[] = [];
    const tileImgs: HTMLImageElement[] = [];
    for (let i = 0; i < FRAME_IDX.length; i++) {
      const tile = el("div", "mob-photogrid__tile mob-photogrid__tile--cgi");
      const img = document.createElement("img");
      img.className = "mob-photogrid__img";
      img.alt = "Sofa";
      img.loading = "lazy";
      img.decoding = "async";
      // A config without 360 frames (colour not rendered yet) → hide that tile, never a
      // broken-image icon. Re-shown on load when a rendered config is picked.
      img.addEventListener("error", () => { tile.style.display = "none"; });
      img.addEventListener("load", () => { tile.style.display = ""; fitPhotogridImg(img); });
      tile.appendChild(img);
      tile.addEventListener("click", () => { if (img.src) openPhotoLightbox(img.src, img.alt); });
      photogrid.appendChild(tile);
      tileEls.push(tile);
      tileImgs.push(img);
    }
    // Swap all 4 tiles to the picked slug's 360 frames on every shape/colour change. The slug
    // is the GLB basename — the SAME key the 360 viewer + baked AR use (can never drift).
    // Même racine et même format que le flipbook : un produit qui déclare `spin` (Amada,
    // frames AVIF ailleurs) garde sa grille photo ; sans `spin`, Bungalow comme avant.
    const FRAMES360 = (product.spin?.base ?? "/perf-assets/bungalow-studio/360") + "/";
    const FRAME_EXT = product.spin?.ext ?? "webp";
    const syncPhotogrid = () => {
      const s = state.get();
      const mat = product.materials?.[0];
      if (!mat) return;
      const cw = currentColorway(mat, s);
      const glb = cw.glbByForme?.[s.formeId] ?? cw.glb;
      if (!glb) return;
      const slug = (glb.split("/").pop() ?? "").replace(/\.glb$/, "");
      for (let i = 0; i < FRAME_IDX.length; i++) {
        const url = `${FRAMES360}${slug}/f${String(FRAME_IDX[i]).padStart(2, "0")}.${FRAME_EXT}`;
        if (tileImgs[i].getAttribute("src") !== url) tileImgs[i].src = url;
      }
    };
    syncPhotogrid();
    tiles.push({ root: photogrid, sync: syncPhotogrid });

    // Placement differs by breakpoint: desktop → inside the hero, under the viewer (so the
    // sticky stage slides over it); phone → at the end of the column, after the CTA.
    const mqDesk = window.matchMedia("(min-width: 920px)");
    const placeGrid = () => { (mqDesk.matches && heroWrap ? heroWrap : root).appendChild(photogrid); };
    placeGrid();
    mqDesk.addEventListener("change", placeGrid);

    // Fade each tile down as the sticky viewer slides over it (desktop only). The stage is
    // opaque, so without this the part still peeking below its hard edge reads as a harsh
    // cut; fading the whole tile makes the pass-over clean (Alex 2026-06-14).
    const stageEl = heroWrap?.querySelector<HTMLElement>(".mob-hero__stage") ?? null;
    let fadeRaf = 0;
    const updateFade = () => {
      fadeRaf = 0;
      if (!mqDesk.matches || !stageEl) { for (const t of tileEls) t.style.opacity = ""; return; }
      const stageBottom = stageEl.getBoundingClientRect().bottom;
      for (const t of tileEls) {
        const overlap = stageBottom - t.getBoundingClientRect().top;
        t.style.opacity = overlap <= 0 ? "1" : String(Math.max(0.1, 1 - overlap / 90));
      }
    };
    const onScrollFade = () => { if (!fadeRaf) fadeRaf = requestAnimationFrame(updateFade); };
    window.addEventListener("scroll", onScrollFade, { passive: true });
    window.addEventListener("resize", onScrollFade, { passive: true });
    requestAnimationFrame(updateFade);
  }

  applyBlocBackground(product, state.get());

  for (const t of tiles) t.sync();
  cta.sync();

  // Out-of-stock greying: repaint when the catalog responds (set by buildCta's hydrate)
  // AND on every selection change, since availability is contextual to shape/fabric.
  repaintOOS = () => applyAvailability(host, product, state.get(), liveAvail);
  repaintOOS();

  // AR ("View in your space") regenerates its USDZ from the live model via the AR
  // controller, which listens to engine content changes — no per-shape file
  // resolution or URL bookkeeping here.
  // Only re-sync the option tiles when a SELECTION actually changes — NOT on every
  // camera move. onCameraChange writes yaw/pitch/zoom into this same state on each
  // mousemove, and re-syncing every tile + the CTA + rewriting the URL on each of those
  // (hundreds/sec with a desktop mouse) is what lagged rotation, badly when moving fast.
  const selSig = (s: ConfigState) =>
    [s.formeId, s.shapeId, s.sizeId, s.materialId, s.colorwayId, s.fabricId, s.legId].join("|");
  const shapeKey = (s: ConfigState) => s.shapeId ?? product.formes.find((f) => f.id === s.formeId)?.shapeId ?? s.formeId;
  let lastSel = selSig(state.get());
  let lastShape = shapeKey(state.get());
  let urlTimer = 0;
  state.subscribe((s) => {
    const sig = selSig(s);
    if (sig !== lastSel) {
      lastSel = sig;
      for (const t of tiles) t.sync();
      cta.sync();
      applyBlocBackground(product, s);
      repaintOOS?.();
      const sk = shapeKey(s);
      if (sk !== lastShape) {
        lastShape = sk;
        // Seat/shape changed → reset the viewer menu so its shape-aware View list is
        // never stale (Alex: "when changing the seat the menu should reset").
        (document.querySelector(".mob-viewer-overlay") as (HTMLElement & { __closeMenu?: () => void }) | null)?.__closeMenu?.();
      }
    }
    // The URL carries the camera too (so share links keep the view), but writing it on
    // every camera frame during a drag is expensive — coalesce to after it settles.
    if (urlTimer) clearTimeout(urlTimer);
    urlTimer = window.setTimeout(() => pushURL(state.get()), 200);
  });

  // Embedded: size the host iframe to the real content (no inner scrollbar / cut-off).
  if (embed) startEmbedAutoHeight();
  // PDP embed: make the viewer follow the HOST page scroll (it can't position:sticky against
  // it from inside the iframe). The embed snippet feeds us the iframe's viewport position.
  if (pdp) setupViewerFollow(heroWrap, cta.root, root);
}

/** PDP embed sticky-follow: translate the hero so it tracks the host page's scroll
 *  (fed by neovaa:viewport from the embed snippet) and releases when it reaches the buy
 *  bar. The hero's own background + z-index make options scroll under it, like sticky. */
function setupViewerFollow(hero: HTMLElement | null, cta: HTMLElement | null, section: HTMLElement | null): void {
  if (!hero || !cta) return;
  const TOP = 12;
  let frameTop = 0;
  let raf = 0;
  hero.style.willChange = "transform";
  const apply = () => {
    raf = 0;
    // Desktop (2-col grid): the viewer is in its own column, so it can stay pinned until the
    // whole section has scrolled by. Mobile (stacked): release before the buy bar so the
    // viewer doesn't cover it.
    const twoCol = !!section && getComputedStyle(section).display === "grid";
    const max = twoCol
      ? Math.max(0, section!.offsetHeight - hero.offsetHeight - 24)
      : Math.max(0, cta.offsetTop - hero.offsetTop - hero.offsetHeight - 16);
    const want = Math.min(Math.max(-frameTop + TOP, 0), max);
    hero.style.transform = want > 0 ? `translateY(${Math.round(want)}px)` : "";
  };
  window.addEventListener("message", (e) => {
    if (e.source !== window.parent) return;
    const d = e.data as { type?: string; frameTop?: number } | null;
    if (!d || d.type !== "neovaa:viewport") return;
    frameTop = Number(d.frameTop) || 0;
    if (!raf) raf = requestAnimationFrame(apply);
  });
}

/* ── Header (logo OR Bloc editorial block) ──────────────────────────────── */

function buildHeader(product: ProductConfig): HTMLElement {
  if (currentTheme() === "bloc") {
    return buildBlocHeader(product);
  }
  const logoBar = el("div", "mob-logobar");
  logoBar.appendChild(brandLogo(product));
  return logoBar;
}

/** Full brand top chrome for the standalone demo: a promo strip + a nav bar
 *  (logo left, nav links, utility icons). Visual only — the links don't route;
 *  it exists to make the page read as the brand's own store. Full-width; the
 *  configurator centers below it. */
function buildBrandChrome(product: ProductConfig): HTMLElement {
  const c = product.theme?.chrome ?? {};
  const chrome = el("header", "mob-chrome");

  if (c.promo) {
    const promo = el("div", "mob-chrome__promo", c.promo);
    if (c.promoBg) promo.style.background = c.promoBg;
    if (c.promoFg) promo.style.color = c.promoFg;
    chrome.appendChild(promo);
  }

  const nav = el("nav", "mob-chrome__nav");
  nav.appendChild(brandLogo(product)); // logo LEFT, like every reference PDP

  const links = el("ul", "mob-chrome__links");
  for (const label of c.nav ?? []) {
    const li = document.createElement("li");
    li.textContent = label;
    if (label === c.saleLabel) li.classList.add("mob-chrome__sale");
    links.appendChild(li);
  }
  nav.appendChild(links);

  const icons = el("div", "mob-chrome__icons");
  icons.innerHTML = CHROME_ICONS;
  nav.appendChild(icons);

  chrome.appendChild(nav);
  return chrome;
}

/** Minimal stroke icons (search / account / wishlist / bag), currentColor. */
const CHROME_ICONS = [
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M12 20s-7-4.6-9.2-9C1.3 8.2 2.6 5 6 5c2 0 3.2 1.3 4 2.6C10.8 6.3 12 5 14 5c3.4 0 4.7 3.2 3.2 6-2.2 4.4-9.2 9-9.2 9z"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M6 8h12l1 12H5L6 8z"/><path d="M9 8a3 3 0 0 1 6 0"/></svg>',
].join("");

/** The brand's real logo (theme.logoUrl) or the NEOVAA wordmark. The standalone
 *  page must read as THE BRAND's page (Alex 2026-06-12); the embed hides this
 *  bar anyway (the host page carries its own header). */
function brandLogo(product: ProductConfig): HTMLElement {
  const url = product.theme?.logoUrl;
  if (url) {
    const img = document.createElement("img");
    img.className = "mob-logo-img";
    img.src = url;
    img.alt = product.productLabel;
    return img;
  }
  return el("span", "mob-logo", "NEOVAA");
}

/** Bloc editorial header — d5-bloc.jsx:289-309 (big display title + rating + NEW). */
function buildBlocHeader(product: ProductConfig): HTMLElement {
  const wrap = el("div", "mob-bloc-header");

  const logoBar = el("div", "mob-logobar");
  logoBar.appendChild(brandLogo(product));
  wrap.appendChild(logoBar);

  const titleWrap = el("div", "mob-bloc-title");
  const h1 = el("h1", "mob-bloc-h1");
  // "Modular" / "Sofa" (inked rotated chip) / "'26" caveat — generic from label.
  const words = product.productLabel.split(" ");
  const last = words.length > 1 ? (words.pop() as string) : product.productLabel;
  const lead = words.join(" ") || "Modular";
  h1.appendChild(el("span", "mob-bloc-lead", lead));
  h1.appendChild(document.createElement("br"));
  h1.appendChild(el("span", "mob-bloc-chip", last));
  h1.appendChild(el("span", "mob-bloc-year", "’26"));
  titleWrap.appendChild(h1);

  const meta = el("div", "mob-bloc-meta");
  const rating = product.rating ?? 4.6;
  const reviews = product.reviewCount ?? 124;
  meta.appendChild(el("span", "mob-bloc-stars", `★★★★★ ${rating} · ${reviews}`));
  meta.appendChild(el("span", "mob-bloc-new", "NEW"));
  titleWrap.appendChild(meta);

  wrap.appendChild(titleWrap);
  return wrap;
}

/* ── Bloc dynamic background (theme-driven, fabric-reactive) ─────────────── */

function activeColorHex(product: ProductConfig, s: ConfigState): string | undefined {
  if (product.materials && product.materials.length > 0) {
    const mat = product.materials.find((m) => m.id === s.materialId) ?? product.materials[0];
    const cw = mat.colorways.find((c) => c.id === s.colorwayId) ?? mat.colorways[0];
    return cw?.color;
  }
  return product.fabrics.find((f) => f.id === s.fabricId)?.color;
}

function applyBlocBackground(product: ProductConfig, s: ConfigState): void {
  if (currentTheme() !== "bloc") return;
  const hex = activeColorHex(product, s);
  if (!hex) return;
  const root = document.documentElement;
  root.style.setProperty("--nv-bg", mix(hex, "#FFFFFF", 0.55));
  root.style.setProperty("--nv-accent", mix(hex, "#000000", 0.25));
  root.style.setProperty("--nv-bloc-shadow", mix(hex, "#000000", 0.35));
}

function mix(a: string, b: string, ratio: number): string {
  const pa = hexToRgb(a);
  const pb = hexToRgb(b);
  if (!pa || !pb) return a;
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * ratio);
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * ratio);
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * ratio);
  return `#${[r, g, bl].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
}

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}


/* ── viewer overlay (fullscreen + menu pill) ─────────────────── */

/** Options threaded into the viewer overlay. `engine` powers the live Dimensions
 *  readout; `ar` routes "View in your space". Both may arrive after an early mount
 *  → wired later by wireOverlayDimensions once available. */
interface ViewerOverlayOpts {
  engine?: Engine;
  ar?: ArController;
}

function injectViewerOverlay(opts?: ViewerOverlayOpts): void {
  const viewer = document.getElementById("viewer");
  if (!viewer) return;
  if (viewer.querySelector(".mob-viewer-overlay")) return;

  const overlay = el("div", "mob-viewer-overlay") as HTMLElement & {
    __engine?: Engine;
    __dims?: DimensionsOverlayApi | null;
    __ar?: ArController;
    __parts?: PartsOverlayApi | null;
    __product?: ProductConfig;
    __closeMenu?: () => void;
  };

  const fs = el("button", "mob-glass-btn mob-fullscreen-btn");
  fs.type = "button";
  fs.setAttribute("aria-label", "Fullscreen");
  fs.appendChild(svgFullscreen());
  // CSS pseudo-fullscreen is the source of truth — the native Fullscreen API is
  // UNAVAILABLE on iPhone Safari for non-video elements (requestFullscreen is
  // undefined → the old `?.()` silently did nothing, which is why the button
  // appeared dead). Toggling a class that pins #viewer to the viewport works on
  // EVERY device; the engine's ResizeObserver on #viewport reframes the canvas.
  // Native fullscreen is attempted best-effort on desktop/Android only, to hide
  // browser chrome — its failure never matters because the class already did the job.
  const setFs = (on: boolean) => {
    viewer.classList.toggle("is-fullscreen", on);
    fs.classList.toggle("is-active", on);
    document.body.classList.toggle("nv-fs-lock", on);
    fs.setAttribute("aria-label", on ? "Exit fullscreen" : "Fullscreen");
    // Swap the glyph: diagonal expand-arrows when normal, an X when fullscreen so
    // it reads as "tap to close / go back".
    fs.replaceChildren(on ? svgClose() : svgFullscreen());
  };
  fs.addEventListener("click", () => {
    const on = !viewer.classList.contains("is-fullscreen");
    setFs(on);
    try {
      const v = viewer as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
      const d = document as Document & {
        webkitExitFullscreen?: () => Promise<void>;
        webkitFullscreenElement?: Element | null;
      };
      if (on) {
        const req = v.requestFullscreen ?? v.webkitRequestFullscreen;
        if (req) void Promise.resolve(req.call(v)).catch(() => { /* CSS already handles it */ });
      } else if (document.fullscreenElement ?? d.webkitFullscreenElement) {
        const exit = document.exitFullscreen ?? d.webkitExitFullscreen;
        if (exit) void Promise.resolve(exit.call(document)).catch(() => { /* ignore */ });
      }
    } catch {
      /* native unsupported (iOS) — the CSS class is the real fullscreen, nothing to do */
    }
  });
  // Keep the class in sync if the OS/browser exits native fullscreen (e.g. Esc, or
  // the swipe-down on Android) so our state doesn't get stuck "on".
  document.addEventListener("fullscreenchange", () => {
    if (!document.fullscreenElement && viewer.classList.contains("is-fullscreen")) {
      // Only auto-exit our CSS state if native was the trigger; on iOS there's no
      // fullscreenElement ever, so this never fires there (CSS stays under button control).
      const hadNative = "requestFullscreen" in viewer;
      if (hadNative) setFs(false);
    }
  });

  if (opts?.engine) overlay.__engine = opts.engine;
  if (opts?.ar) overlay.__ar = opts.ar;

  // Lazily build the dimensions overlay (SVG measurement lines + label pills, the
  // prod-viewer look) on first toggle — needs the engine, which may arrive after
  // an early hero mount. Reuses whatever engine has been wired by then.
  const ensureDims = (): DimensionsOverlayApi | null => {
    if (overlay.__dims) return overlay.__dims;
    const eng = overlay.__engine;
    if (!eng) return null;
    overlay.__dims = createDimensionsOverlay(eng, viewer, {
      // A real tap on the viewer clears Dimensions: hide it, restore the big framing,
      // and un-light the menu item — same end state as toggling it off from the menu.
      onDismiss: () => {
        dimsItem.classList.remove("is-active");
        overlay.__engine?.restoreHome();
        trackEvent("dimensions_toggle", { on: false });
      },
    });
    return overlay.__dims;
  };

  const menuWrap = el("div", "mob-menu-wrap");
  const menuBtn = el("button", "mob-glass-pill");
  menuBtn.type = "button";
  menuBtn.setAttribute("aria-expanded", "false");
  menuBtn.appendChild(svgMenu());
  menuBtn.appendChild(el("span", undefined, "Menu"));

  const panel = el("div", "mob-menu-panel");
  // Two-level liquid-glass nav: a clipped, height-morphing STAGE holds a MAIN pane
  // and a DETAIL pane. "Details ›" pushes to detail (motion-crafted slide + fade +
  // height morph); the ‹ back arrow pops to main. The dots follow the detail pane.
  const stage = el("div", "mob-menu-stage");
  const mainPane = el("div", "mob-menu-pane mob-menu-pane--main is-active");
  const detailPane = el("div", "mob-menu-pane mob-menu-pane--detail");
  const viewPane = el("div", "mob-menu-pane mob-menu-pane--view");

  const ensureParts = (): PartsOverlayApi | null => {
    if (overlay.__parts) return overlay.__parts;
    const eng = overlay.__engine;
    // Cap at 4 focus points so the detail list never runs off a short viewer.
    const parts = (overlay.__product?.hotspots ?? []).slice(0, 4);
    if (!eng || parts.length === 0) return null;
    // The modal's "Reset view" exits Details: the overlay clears the camera + dots,
    // and onExit returns the menu to its main pane.
    overlay.__parts = createPartsOverlay(eng, viewer, parts, { onExit: () => setView("main") });
    return overlay.__parts;
  };

  let view: "main" | "detail" | "view" = "main";
  function setView(next: "main" | "detail" | "view"): void {
    view = next;
    mainPane.classList.toggle("is-active", next === "main");
    detailPane.classList.toggle("is-active", next === "detail");
    viewPane.classList.toggle("is-active", next === "view");
    // Morph the stage height to the active pane (panes are absolutely stacked for
    // the slide, so the stage needs an explicit, animatable height).
    const active = next === "main" ? mainPane : next === "detail" ? detailPane : viewPane;
    stage.style.height = `${active.scrollHeight}px`;
  }

  // ── MAIN pane: View in your space · Dimensions · View › · Details › ──
  mainPane.appendChild(menuItem("View in your space", svgAr(), () => {
    trackEvent("ar_click", {});
    closeMenu();
    overlay.__ar?.launch();
  }));
  mainPane.appendChild(menuSeparator());
  const dimsItem = menuItem("Dimensions", svgDims(), () => {
    const ov = ensureDims();
    if (!ov) { showToast("Dimensions unavailable"); closeMenu(); return; }
    const on = !ov.isActive();
    ov.setActive(on);
    dimsItem.classList.toggle("is-active", on);
    // Dimensions ON → dolly the camera OUT so the whole item + its measurement
    // labels (which sit outside the box) fit inside the viewer; OFF → dolly back to
    // the big default framing. Both keep the user's current orbit angle.
    if (on) overlay.__engine?.zoomOutToFit();
    else overlay.__engine?.restoreHome();
    trackEvent("dimensions_toggle", { on });
    closeMenu();
  });
  mainPane.appendChild(dimsItem);
  mainPane.appendChild(menuSeparator());
  const viewNav = menuItem("View", svgEye(), () => { buildViewRows(); setView("view"); });
  viewNav.classList.add("mob-menu-item--nav");
  viewNav.appendChild(svgChevron());
  mainPane.appendChild(viewNav);
  mainPane.appendChild(menuSeparator());
  const detailsNav = menuItem("Details", svgParts(), () => {
    const ov = ensureParts();
    if (!ov) { showToast("No details for this product"); return; }
    buildDetailRows();
    ov.setActive(true); // dots appear together with the detail pane
    setView("detail");
    trackEvent("parts_open", {});
  });
  detailsNav.classList.add("mob-menu-item--nav");
  detailsNav.appendChild(svgChevron());
  mainPane.appendChild(detailsNav);

  // ── DETAIL pane: ‹ back header + part rows + Reset (rows built lazily) ──
  const backBtn = el("button", "mob-menu-back");
  backBtn.type = "button";
  backBtn.setAttribute("aria-label", "Back to menu");
  backBtn.appendChild(svgChevronBack());
  backBtn.appendChild(el("span", "mob-menu-back__title", "Details"));
  backBtn.addEventListener("click", () => {
    overlay.__parts?.resetView(); // full exit: camera reset + dots off + modal closed
    setView("main");
    trackEvent("parts_close", {});
  });
  detailPane.appendChild(backBtn);

  let detailRowsBuilt = false;
  function buildDetailRows(): void {
    if (detailRowsBuilt) return;
    detailRowsBuilt = true;
    // Max 4 focus rows; no Reset row here (Reset lives in the modal). Tapping a part
    // focuses it, opens the modal, and closes the menu — reopening returns here.
    for (const p of (overlay.__product?.hotspots ?? []).slice(0, 4)) {
      const row = menuItem(p.label, svgDot(), () => {
        overlay.__parts?.focusPart(p.id);
        closeMenu();
      });
      row.classList.add("mob-menu-item--sub");
      detailPane.appendChild(row);
    }
  }

  // ── VIEW pane: ‹ back header + shape-aware focal rows (rebuilt on every open) ──
  const viewBack = el("button", "mob-menu-back");
  viewBack.type = "button";
  viewBack.setAttribute("aria-label", "Back to menu");
  viewBack.appendChild(svgChevronBack());
  viewBack.appendChild(el("span", "mob-menu-back__title", "View"));
  viewBack.addEventListener("click", () => setView("main"));
  viewPane.appendChild(viewBack);

  // Views adapt to the ACTUAL forme: Left/Right always; "Long chair" only when the
  // forme has a chaise (sh-l*/sh-u*); "Corner" only when it has a corner (sh-c*); both
  // if a forme ever had both. Each view is just the part's centre — centerOnRegion
  // re-centers there keeping the current angle (no special zoom/angle per Alex).
  function focalViewsForShape(shapeId: string): { id: string; label: string; anchor: { x: number; y: number; z: number } }[] {
    const rightSide = /r$/.test(shapeId);
    const hasChaise = /^sh-(l|u)/.test(shapeId);
    const hasCorner = /^sh-c/.test(shapeId);
    const v = [
      { id: "left", label: "Left", anchor: { x: 0.2, y: 0.5, z: 0.5 } },
      { id: "right", label: "Right", anchor: { x: 0.8, y: 0.5, z: 0.5 } },
    ];
    if (hasChaise) v.push({ id: "chaise", label: "Long chair", anchor: { x: rightSide ? 0.74 : 0.26, y: 0.5, z: 0.62 } });
    if (hasCorner) v.push({ id: "corner", label: "Corner", anchor: { x: rightSide ? 0.34 : 0.66, y: 0.5, z: 0.38 } });
    return v;
  }
  function buildViewRows(): void {
    // rebuild every open so it reflects the CURRENT shape; keep the back button (first child)
    while (viewPane.children.length > 1) viewPane.removeChild(viewPane.lastChild as Node);
    // No state signal in this overlay scope: the live shape is the selected shape
    // tile (.mob-shape-btn.is-selected), with a fall-back to the product's default
    // forme shapeId. Universal Left/Right always show even when shapeId is unknown.
    const product = overlay.__product;
    const eng = overlay.__engine;
    // Source of truth = the forme the ENGINE actually built (live state), NOT a DOM
    // query for .mob-shape-btn.is-selected — that was stale when the menu opened and
    // fell back to the first forme with a shapeId (often a corner), so a 2-seater +
    // long-chair wrongly showed "Corner". Reading currentFormeId fixes that.
    // currentFormeId is undefined until the first build completes. The menu only
    // opens on an explicit tap (well after load), but guard anyway: no forme → empty
    // shapeId → focalViewsForShape falls back to the universal Left/Right (graceful).
    const formeId = eng?.currentFormeId;
    const forme = formeId ? product?.formes?.find((f) => f.id === formeId) : undefined;
    const shapeId = forme?.shapeId ?? forme?.id ?? "";
    // Picking a view does NOT close the menu — the user stays in the View pane to
    // switch between angles (Alex: "we are still in the view menu"). The active row is
    // highlighted; tapping the viewer (or changing the seat) closes + resets the menu.
    const rows: HTMLElement[] = [];
    const markActive = (active: HTMLElement) => { for (const r of rows) r.classList.toggle("is-active", r === active); };
    const overview = menuItem("Overview", svgDot(), () => { eng?.resetView(); markActive(overview); });
    overview.classList.add("mob-menu-item--sub", "is-active"); // default: full view
    viewPane.appendChild(overview);
    rows.push(overview);
    for (const fv of focalViewsForShape(shapeId)) {
      const row = menuItem(fv.label, svgDot(), () => { eng?.centerOnRegion(fv.anchor); markActive(row); });
      row.classList.add("mob-menu-item--sub");
      viewPane.appendChild(row);
      rows.push(row);
    }
  }

  stage.append(mainPane, detailPane, viewPane);
  panel.appendChild(stage);

  function openMenu() {
    overlay.__parts?.dismissCard(); // opening the menu closes any open part window
    menuWrap.classList.add("is-open");
    menuBtn.setAttribute("aria-expanded", "true");
    // scrollHeight is 0 while display:none — seed the stage height now it's shown so
    // the panel opens at the right size and the first push animates from it.
    const active = view === "main" ? mainPane : view === "detail" ? detailPane : viewPane;
    stage.style.height = `${active.scrollHeight}px`;
  }
  function closeMenu() {
    menuWrap.classList.remove("is-open");
    menuBtn.setAttribute("aria-expanded", "false");
    // Closing always returns the menu to its top pane, so it never reopens stale in
    // the View/Details sub-pane (and a seat change can reset it from outside).
    setView("main");
  }
  overlay.__closeMenu = closeMenu;
  menuBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    if (menuWrap.classList.contains("is-open")) closeMenu();
    else openMenu();
  });
  document.addEventListener("click", (e) => {
    if (!menuWrap.contains(e.target as Node)) closeMenu();
  });

  menuWrap.append(menuBtn, panel);

  // "HD incoming" mark — a switch shows the low-poly proxy INSTANTLY; this
  // tells the shopper the crisp version is on its way instead of silently
  // showing the rough model. NOT a button, NOT a progress bar, small and
  // stealthy (Alex 2026-06-12): the SOFA'S OWN CONTOUR, one continuous fine
  // stroke that draws itself as the real bytes of the full model arrive,
  // completes exactly at the crisp swap, and fades. Non-interactive
  // (pointer-events: none — orbit/pinch pass through). Wired to the engine
  // here or later by wireOverlayDimensions.
  const hd = el("div", "mob-hd-thread");
  hd.setAttribute("aria-hidden", "true");
  const svgNS = "http://www.w3.org/2000/svg";
  const hdSvg = document.createElementNS(svgNS, "svg");
  hdSvg.setAttribute("viewBox", "0 0 36 18");
  // Bigger so the "still loading the crisp model" signal is clearly visible (Alex).
  hdSvg.setAttribute("width", "54");
  hdSvg.setAttribute("height", "27");
  hdSvg.setAttribute("fill", "none");
  // One continuous sofa profile: left side up, rounded arm, dip to the seat,
  // seat line, up and over the right arm, down. Open at the bottom so the
  // draw visually "completes" the sofa.
  const SOFA_CONTOUR =
    "M2 16 L2 8 Q2 4 6 4 Q9 4 9 7 L9 10 L27 10 L27 7 Q27 4 30 4 Q34 4 34 8 L34 16";
  for (const cls of ["mob-hd-thread__track", "mob-hd-thread__line"]) {
    const path = document.createElementNS(svgNS, "path");
    path.setAttribute("d", SOFA_CONTOUR);
    path.setAttribute("pathLength", "100");
    path.setAttribute("class", cls);
    hdSvg.appendChild(path);
  }
  hd.append(hdSvg);

  overlay.append(fs, menuWrap, hd);
  viewer.appendChild(overlay);
  if (opts?.engine) wireHdChip(overlay, opts.engine);
}

/** Hook the HD thread to the engine: stage 'proxy' arms it (250 ms grace so a
 *  cache-hit switch never flashes it), real download progress draws the loops
 *  (capped at 96% — the last stretch is the decode), stage 'full' completes the
 *  stroke and fades it out. Idempotent (one wire per overlay). */
function wireHdChip(
  overlay: HTMLElement & { __hdWired?: boolean; __product?: ProductConfig },
  engine: Engine,
): void {
  if (overlay.__hdWired) return;
  const hd = overlay.querySelector(".mob-hd-thread") as HTMLElement | null;
  if (!hd) return;
  overlay.__hdWired = true;

  // Brand mode (Alex 2026-06-12: "their logo as the loading animation"): when the
  // product carries a brand logo, the indicator IS the logo — a faint ghost with
  // a solid copy revealed left-to-right by the real download progress.
  const logoUrl = overlay.__product?.theme?.logoUrl;
  if (logoUrl && !hd.querySelector(".mob-hd-logo")) {
    hd.replaceChildren();
    hd.classList.add("mob-hd-thread--logo");
    const ghost = document.createElement("img");
    ghost.className = "mob-hd-logo mob-hd-logo--ghost";
    ghost.src = logoUrl;
    ghost.alt = "";
    const fill = el("div", "mob-hd-logo__fill");
    const solid = document.createElement("img");
    solid.className = "mob-hd-logo";
    solid.src = logoUrl;
    solid.alt = "";
    fill.appendChild(solid);
    hd.append(ghost, fill);
  }
  const line = hd.querySelector(".mob-hd-thread__line") as SVGPathElement | null;

  let target = 0;
  let shown = 0;
  let raf = 0;
  let showTimer = 0;
  let active = false;
  const SHOW_DELAY_MS = 250;

  const render = () => {
    shown += (target - shown) * 0.14;
    if (target >= 1 && shown > 0.995) shown = 1;
    const p = Math.max(0, Math.min(1, shown));
    if (line) line.style.strokeDashoffset = String(100 - p * 100);
    hd.style.setProperty("--nv-hd-p", String(p)); // logo-wipe width driver
    if (!active && Math.abs(target - shown) < 0.002) {
      raf = 0;
      return;
    }
    raf = requestAnimationFrame(render);
  };
  const ensureRaf = () => {
    if (!raf) raf = requestAnimationFrame(render);
  };
  const start = () => {
    active = true;
    target = 0.05; // immediate hint of life the moment the thread appears
    shown = 0;
    window.clearTimeout(showTimer);
    showTimer = window.setTimeout(() => {
      if (active) hd.classList.add("is-on");
    }, SHOW_DELAY_MS);
    ensureRaf();
  };
  const finish = () => {
    active = false;
    window.clearTimeout(showTimer);
    target = 1; // complete the stroke, then fade
    ensureRaf();
    window.setTimeout(() => hd.classList.remove("is-on"), 300);
  };

  engine.onLodStage((stage) => (stage === "proxy" ? start() : finish()));
  engine.onLodProgress((p) => {
    // Real bytes → real loops. -1 (no content-length) still creeps with each
    // network chunk, so motion stays tied to actual transfer, never a fake timer.
    target = p < 0 ? Math.min(0.88, target + 0.02) : Math.max(target, p * 0.96);
    ensureRaf();
  });
  // Cold-load case: the proxy may already be on screen by the time the engine
  // gets wired — reflect the current stage, don't wait for the next transition.
  if (typeof window !== "undefined" && window.__NEOVAA_LOD_STAGE__ === "proxy") start();
}

/** Attach the engine to an already-built overlay (the early hero mount can build
 *  the overlay before the engine exists). Dimensions builds lazily on first
 *  toggle and reads this engine. Idempotent. No-op if no overlay. */
function wireOverlayDimensions(engine: Engine, ar?: ArController, product?: ProductConfig): void {
  const overlay = document.querySelector(".mob-viewer-overlay") as
    | (HTMLElement & { __engine?: Engine; __ar?: ArController; __product?: ProductConfig })
    | null;
  if (!overlay) return;
  overlay.__engine = engine;
  if (ar) overlay.__ar = ar;
  if (product) overlay.__product = product;
  wireHdChip(overlay, engine);
}


/* ── 01 · Shape tile (silhouette tiles + see-more) ───────────── */

interface ShapeCtx {
  product: ProductConfig;
  /** The Shape axis options (real `product.shapes`, or formes-as-shapes fallback). */
  shapes: ShapeOption[];
  state: Signal<ConfigState>;
  engine: Engine;
  lru: LruWindows;
  host: HTMLElement;
  onPick: (shapeId: string) => void;
}

/** The Shape axis options. Real `product.shapes` when present; otherwise (v4 /
 *  POC / Quaternius compat) the formes ARE the shapes — synthesize a ShapeOption
 *  per forme so the picker keeps working unchanged with no size axis. */
function resolveShapeList(product: ProductConfig): ShapeOption[] {
  if (product.shapes && product.shapes.length > 0) return product.shapes;
  return product.formes.map((f) => ({ id: f.id, label: f.label, featured: f.featured }));
}

/** Map a SHAPE to seat count + chaise flag for the silhouette. Resolves a
 *  representative forme for the shape (any forme tagged with this shapeId, else
 *  the forme whose id === shapeId for the v4 path), then reads its module count /
 *  label. Heuristic, config-driven — no Bangalow-specific code. */
function silhouetteForShape(
  product: ProductConfig,
  shape: ShapeOption,
): { seats: number; chaise: boolean } {
  const forme =
    product.formes.find((f) => f.shapeId === shape.id) ??
    product.formes.find((f) => f.id === shape.id);
  // Combine the shape label + the resolved forme label so an explicit seat count
  // on either wins (e.g. shape "2 Seater", or forme "2 Seats (1×)").
  const label = `${shape.label} ${forme?.label ?? ""}`.toLowerCase();
  const chaise = /chaise|l-?shape|corner|u-?shape/.test(label);
  const m = /(\d+)\s*(?:place|seat)/.exec(label);
  const count = forme?.modules.length ?? 1;
  const seats = m ? parseInt(m[1], 10) : Math.max(2, count * 2);
  return { seats: Math.min(seats, 6), chaise };
}

function buildShapeTile(ctx: ShapeCtx): TileApi {
  const list = ctx.shapes;
  const tile = createGlassTile({
    stepLabel: "01 · Shape",
    onSeeMore: () => openModal(),
  });

  const row = el("div", "mob-tile-row mob-tile-row--shapes");
  tile.body.appendChild(row);

  const inkColor = () => (currentTheme() === "spatial-dark" ? "#e8e9ee" : currentTheme() === "bloc" ? "#1A1612" : "#0A0B10");

  // Colour-aware CGI tile image: fills the product's shapeImageTemplate with the
  // shape id + the CURRENT colorway so the tiles show the actual selected fabric.
  const shapeImgSrc = (shapeId: string): string | undefined => {
    const tpl = ctx.product.shapeImageTemplate;
    if (!tpl) return undefined;
    const cw = ctx.state.get().colorwayId ?? ctx.product.materials?.[0]?.colorways?.[0]?.id ?? "";
    return tpl.replace("{shape}", shapeId).replace("{colorway}", cw);
  };
  const makeSil = (shape: ShapeOption, shadow: string): SVGElement => {
    const sil = silhouetteForShape(ctx.product, shape);
    return sofaSVG({ seats: sil.seats, chaise: sil.chaise, body: inkColor(), legs: inkColor(), shadow, arm: "soft" });
  };

  const rebuildRow = () => {
    row.innerHTML = "";
    const visible = list.length > 4 ? ctx.lru.forme : list.map((_, j) => j);
    for (const i of visible) {
      const shape = list[i];
      if (!shape) continue;
      const btn = el("button", "mob-shape-btn");
      btn.type = "button";
      btn.dataset.shapeIdx = String(i);
      btn.dataset.shapeId = shape.id;
      btn.title = shape.label;
      const imgSrc = shapeImgSrc(shape.id) ?? shape.image;
      if (imgSrc) {
        // Real CGI render of the shape (colour-aware), replaces the silhouette.
        const im = document.createElement("img");
        im.src = imgSrc; im.alt = shape.label; im.loading = "lazy";
        im.className = "mob-shape-btn__img";
        im.onerror = () => { im.remove(); btn.insertBefore(makeSil(shape, "transparent"), btn.firstChild); };
        btn.appendChild(im);
      } else {
        btn.appendChild(makeSil(shape, "transparent"));
      }
      // Shape label inside the button — hidden in the default card layout, shown
      // by skins that render the selector as labelled ROWS (Castlery's pattern).
      btn.appendChild(el("span", "mob-shape-btn__label", shape.label));
      btn.addEventListener("click", () => commitPick(i));
      row.appendChild(btn);
    }
  };

  let modal: ModalSheet | null = null;
  function openModal(): void {
    if (!modal) {
      modal = createModalSheet({ host: ctx.host, background: themeSheetBg(), onDismiss: () => {} });
    }
    modal.setTitle("All shapes", String(list.length));
    modal.clearBody();
    const grid = el("div", "mob-modal-grid mob-modal-grid--2");
    for (let i = 0; i < list.length; i++) {
      const shape = list[i];
      const sel = currentShapeId(ctx) === shape.id;
      const btn = el("button", "mob-modal-tile mob-modal-tile--shape" + (sel ? " is-selected" : ""));
      btn.type = "button";
      const silWrap = el("span", "mob-modal-tile__sil");
      const imgSrc = shapeImgSrc(shape.id) ?? shape.image;
      if (imgSrc) {
        const im = document.createElement("img");
        im.src = imgSrc; im.alt = shape.label; im.loading = "lazy";
        im.className = "mob-modal-tile__img";
        im.onerror = () => { im.remove(); silWrap.appendChild(makeSil(shape, "rgba(0,0,0,0.15)")); };
        silWrap.appendChild(im);
      } else {
        silWrap.appendChild(makeSil(shape, "rgba(0,0,0,0.15)"));
      }
      btn.appendChild(silWrap);
      const meta = el("span", "mob-modal-tile__meta");
      meta.appendChild(el("span", "mob-modal-tile__name", shape.label));
      btn.appendChild(meta);
      btn.addEventListener("click", () => {
        commitPick(i);
        modal?.close();
      });
      grid.appendChild(btn);
    }
    modal.body.appendChild(grid);
    modal.open();
  }

  function commitPick(newIdx: number): void {
    ctx.lru.forme = pushLru(ctx.lru.forme, newIdx, list.length);
    rebuildRow();
    const shape = list[newIdx];
    if (shape) ctx.onPick(shape.id);
  }

  rebuildRow();
  tile.setSeeMoreVisible(list.length > 4);

  return {
    root: tile.root,
    sync() {
      const sel = currentShapeId(ctx);
      for (const btn of row.querySelectorAll<HTMLButtonElement>(".mob-shape-btn")) {
        btn.classList.toggle("is-selected", btn.dataset.shapeId === sel);
        // Colour changed → repoint each tile image at the new colorway's render.
        const im = btn.querySelector<HTMLImageElement>(".mob-shape-btn__img");
        const sid = btn.dataset.shapeId;
        if (im && sid) { const src = shapeImgSrc(sid); if (src) im.src = src; }
      }
      tile.setLabelValue(list.find((s) => s.id === sel)?.label ?? "");
    },
  };
}

/** The active shape id: state.shapeId when set, else derived from the current
 *  forme's shapeId tag, else the forme id (v4 path). Keeps the tile highlighted
 *  even before the first explicit shape pick. */
function currentShapeId(ctx: ShapeCtx): string | undefined {
  const s = ctx.state.get();
  if (s.shapeId) return s.shapeId;
  const forme = ctx.product.formes.find((f) => f.id === s.formeId);
  return forme?.shapeId ?? forme?.id;
}

/* ── Size sub-row (4 pills + dim readout) ─────────────────────── */

interface SizeCtx {
  product: ProductConfig;
  state: Signal<ConfigState>;
  engine: Engine;
  onPick: (sizeId: string) => void;
}

function buildSizeRow(ctx: SizeCtx): TileApi {
  const sizes = ctx.product.sizes ?? [];
  const root = el("div", "mob-size");

  const head = el("div", "mob-size__head");
  head.appendChild(el("span", "mob-size__label", "Size"));
  const dimEl = el("span", "mob-size__dim", "");
  head.appendChild(dimEl);
  root.appendChild(head);

  const rowEl = el("div", "mob-size__row");
  root.appendChild(rowEl);

  for (const sz of sizes) {
    const btn = el("button", "mob-size__pill");
    btn.type = "button";
    btn.dataset.sizeId = sz.id;
    btn.title = sz.dim;
    btn.textContent = sz.label;
    btn.addEventListener("click", () => ctx.onPick(sz.id));
    rowEl.appendChild(btn);
  }

  return {
    root,
    sync() {
      const s = ctx.state.get();
      // Size is its own axis now — read state.sizeId directly (no formeId coupling).
      const active = sizes.find((z) => z.id === s.sizeId) ?? sizes[0];
      dimEl.textContent = active?.dim ?? "";
      for (const btn of rowEl.querySelectorAll<HTMLButtonElement>(".mob-size__pill")) {
        btn.classList.toggle("is-selected", btn.dataset.sizeId === active?.id);
      }
    },
  };
}

/* ── 02 · Material + 03 · Colorway (two-level fabric) ─────────── */

interface FabricCtx {
  product: ProductConfig;
  state: Signal<ConfigState>;
  engine: Engine;
  lru: LruWindows;
  host: HTMLElement;
  model: FabricModel;
}

/** Apply (material, colorway) to the 3D via the engine's single FabricSwatch. */
function applyFabricPair(ctx: FabricCtx): void {
  const s = ctx.state.get();
  const mat = currentMaterial(ctx.model, s);
  const cw = currentColorway(mat, s);
  ctx.engine.applyFabric(swatchFor(mat, cw));
}

function finishOf(m: MaterialOption): string {
  return m.finish ?? finishFromId(m.id);
}

function buildMaterialTile(ctx: FabricCtx): TileApi {
  const list = ctx.model.materials;
  const tile = createGlassTile({
    stepLabel: "02 · Material",
    onSeeMore: () => openModal(),
  });

  const row = el("div", "mob-tile-row mob-tile-row--swatches");
  tile.body.appendChild(row);

  const rebuildRow = () => {
    row.innerHTML = "";
    const selId = ctx.state.get().materialId;
    const visible = list.length > 4 ? ctx.lru.material : list.map((_, j) => j);
    for (const i of visible) {
      const mat = list[i];
      if (!mat) continue;
      // Material tile fill: the material's own sample (first colorway) over its texture.
      const sample = mat.colorways[0]?.color ?? "#cccccc";
      const sw = squareSwatch({
        id: mat.id,
        label: mat.label,
        finish: finishOf(mat),
        hex: sample,
        // La vignette du materiau montre SA texture quand le config en fournit une
        // (Amada : macro du tissu teintee) ; sans thumbUrl, fond procedural comme avant.
        image: mat.thumbUrl,
        dataAttr: "materialId",
        onClick: () => commitPick(i),
      });
      // rebuildRow() runs at the END of commitPick (after state.set), so flag the
      // selected material HERE — otherwise it shows no highlight until the next state
      // change (e.g. a colorway click). Mirrors the colorway tile (v49 fix).
      if (mat.id === selId) sw.classList.add("is-selected");
      row.appendChild(sw);
    }
  };

  let modal: ModalSheet | null = null;
  function openModal(): void {
    if (!modal) {
      modal = createModalSheet({ host: ctx.host, background: themeSheetBg(), onDismiss: () => {} });
    }
    modal.setTitle("All materials", String(list.length));
    modal.clearBody();
    const grid = el("div", "mob-modal-grid mob-modal-grid--3");
    for (let i = 0; i < list.length; i++) {
      const mat = list[i];
      const sel = ctx.state.get().materialId === mat.id;
      const sample = mat.colorways[0]?.color ?? "#cccccc";
      const visual = optionHasVisual("material", mat.id, ctx.product, ctx.state.get());
      grid.appendChild(
        modalSquareTile({
          label: mat.label,
          finish: finishOf(mat),
          hex: sample,
          selected: sel,
          oos: !visual,
          oosMsg: visual ? undefined : `${mat.label} isn't available for this shape yet.`,
          onClick: () => {
            commitPick(i);
            modal?.close();
          },
        }),
      );
    }
    modal.body.appendChild(grid);
    modal.open();
  }

  function commitPick(newIdx: number): void {
    ctx.lru.material = pushLru(ctx.lru.material, newIdx, list.length);
    const mat = list[newIdx];
    if (!mat) return;
    // Picking a material lands on its first colorway (mockup pickMaterial), or on the
    // first one that exists for the current shape on a 360-first product (velvet is
    // rendered for 6 Amada shapes so far : index 0 could have no spin at all).
    const s = ctx.state.get();
    const idx0 = Math.max(0, mat.colorways.findIndex((c) => optionHasVisual("colorway", c.id, ctx.product, { ...s, materialId: mat.id })));
    const cw0 = mat.colorways[idx0];
    ctx.lru.colorway = initialWindow(mat.colorways.length, idx0);
    ctx.state.set({ ...s, materialId: mat.id, colorwayId: cw0?.id, fabricId: cw0?.id ?? s.fabricId });
    trackEvent("material_change", { to: mat.id });
    applyFabricPair(ctx);
    // Smooth swap: state.set already re-flagged the selected swatch via sync(), so the
    // halo/lift CSS-transitions across the existing buttons. Only rebuild if the pick
    // wasn't on screen (windowed >4 list) — destroying the row would kill the anim.
    if (!row.querySelector(`.mob-swatch[data-material-id="${mat.id}"]`)) rebuildRow();
  }

  rebuildRow();
  tile.setSeeMoreVisible(list.length > 4);

  return {
    root: tile.root,
    sync() {
      const sel = ctx.state.get().materialId;
      for (const btn of row.querySelectorAll<HTMLButtonElement>(".mob-swatch")) {
        btn.classList.toggle("is-selected", btn.dataset.materialId === sel);
      }
    },
  };
}

function buildColorwayTile(ctx: FabricCtx): TileApi {
  const tile = createGlassTile({
    stepLabel: "03 · Colorway",
    // see-more is dynamic: a material with >4 colorways needs it. We always wire
    // a handler and let it open if the active material has >4.
    onSeeMore: () => openModal(),
  });

  const row = el("div", "mob-tile-row mob-tile-row--swatches mob-tile-row--pills");
  tile.body.appendChild(row);

  function activeMaterial(): MaterialOption {
    return currentMaterial(ctx.model, ctx.state.get());
  }

  // Track the last material the row was built for. The colorway list only
  // changes when the material changes, so sync() can skip the full rebuild
  // (and the texture re-paint it triggers) for unrelated state ticks — price,
  // leg, shape, size — and just re-flag the selected swatch instead.
  let renderedMatId: string | null = null;

  const rebuildRow = () => {
    row.innerHTML = "";
    const mat = activeMaterial();
    renderedMatId = mat.id;
    const cws = mat.colorways;
    // Keep the colorway window valid for the current material length.
    const sel = idxOf(cws, ctx.state.get().colorwayId);
    if (ctx.lru.colorway.some((i) => i >= cws.length)) {
      ctx.lru.colorway = initialWindow(cws.length, sel);
    }
    // `showAllColorways` : la rangee montre TOUS les coloris, sans fenetre ni "see more"
    // (Amada, 10 coloris, Alex 2026-09-07). Absent → fenetre de 4 comme avant.
    const showAll = !!ctx.product.showAllColorways;
    const visible = cws.length > 4 && !showAll ? ctx.lru.colorway : cws.map((_, j) => j);
    const selId = ctx.state.get().colorwayId;
    for (const i of visible) {
      const cw = cws[i];
      if (!cw) continue;
      const sw = squareSwatch({
        id: cw.id,
        label: cw.label,
        finish: finishOf(mat),
        hex: cw.color,
        image: cw.image,
        dataAttr: "colorwayId",
        onClick: () => commitPick(i),
      });
      // rebuildRow() replaces the buttons, so selection must be applied here
      // (unlike shape/material/leg, the colorway row is rebuilt on every sync
      // because switching material changes the colorway list). Without this the
      // active colorway shows no border/scale highlight — v49 break.
      if (cw.id === selId) sw.classList.add("is-selected");
      row.appendChild(sw);
    }
    // Top-right shows "see more" only when this material has more colorways than the
    // 4 shown inline (Alex 2026-06-01: see-more if more, else nothing, never the name).
    tile.setSeeMoreVisible(cws.length > 4 && !showAll);
  };

  /** Re-flag the selected colorway without rebuilding the row (cheap path). */
  const markSelected = () => {
    const selId = ctx.state.get().colorwayId;
    for (const sw of row.querySelectorAll<HTMLButtonElement>(".mob-swatch")) {
      sw.classList.toggle("is-selected", sw.dataset.colorwayId === selId);
    }
    tile.setLabelValue(activeMaterial().colorways.find((c) => c.id === selId)?.label ?? "");
  };

  let modal: ModalSheet | null = null;
  function openModal(): void {
    const mat = activeMaterial();
    if (mat.colorways.length <= 4) return;
    if (!modal) {
      modal = createModalSheet({ host: ctx.host, background: themeSheetBg(), onDismiss: () => {} });
    }
    modal.setTitle(`${mat.label} colors`, String(mat.colorways.length));
    modal.clearBody();
    const grid = el("div", "mob-modal-grid mob-modal-grid--3");
    for (let i = 0; i < mat.colorways.length; i++) {
      const cw = mat.colorways[i];
      const sel = ctx.state.get().colorwayId === cw.id;
      const visual = optionHasVisual("colorway", cw.id, ctx.product, ctx.state.get());
      grid.appendChild(
        modalSquareTile({
          label: cw.label,
          finish: finishOf(mat),
          hex: cw.color,
          selected: sel,
          oos: !visual || !isOptionAvailable("colorway", cw.id, ctx.product, ctx.state.get(), liveAvail),
          oosMsg: visual ? undefined : `${cw.label} isn't available for this shape yet.`,
          onClick: () => {
            commitPick(i);
            modal?.close();
          },
        }),
      );
    }
    modal.body.appendChild(grid);
    modal.open();
  }

  function commitPick(newIdx: number): void {
    const mat = activeMaterial();
    const cw = mat.colorways[newIdx];
    if (!cw) return;
    ctx.lru.colorway = pushLru(ctx.lru.colorway, newIdx, mat.colorways.length);
    const s = ctx.state.get();
    ctx.state.set({ ...s, colorwayId: cw.id, fabricId: cw.id });
    trackEvent("colorway_change", { to: cw.id });
    applyFabricPair(ctx);
    // Smooth swap: sync() already markSelected() the row, so skip the destroy-rebuild
    // when the picked colorway is on screen (keeps the CSS transition flowing).
    if (!row.querySelector(`.mob-swatch[data-colorway-id="${cw.id}"]`)) rebuildRow();
  }

  rebuildRow();

  return {
    root: tile.root,
    sync() {
      const mat = activeMaterial();
      // Only rebuild when the active material changed (that's the only thing
      // that changes the colorway list / texture). Otherwise just re-flag the
      // selected swatch — avoids re-painting all swatch textures on every tick.
      if (mat.id !== renderedMatId) rebuildRow();
      else markSelected();
    },
  };
}

/* ── 04 · Legs (SQUARE finish tiles — supersedes mockup circles) ── */

interface LegCtx {
  product: ProductConfig;
  state: Signal<ConfigState>;
  engine: Engine;
  lru: LruWindows;
  host: HTMLElement;
  onPick: (id: string) => void;
}

function legFinish(leg: LegOption): string {
  return leg.finish ?? finishFromId(leg.id);
}

function buildLegTile(ctx: LegCtx): TileApi {
  const list = ctx.product.legs;
  const tile = createGlassTile({
    stepLabel: "04 · Legs",
    onSeeMore: () => openModal(),
  });

  const row = el("div", "mob-tile-row mob-tile-row--swatches");
  tile.body.appendChild(row);

  const rebuildRow = () => {
    row.innerHTML = "";
    const visible = list.length > 4 ? ctx.lru.leg : list.map((_, j) => j);
    for (const i of visible) {
      const leg = list[i];
      if (!leg) continue;
      row.appendChild(
        squareSwatch({
          id: leg.id,
          label: leg.label,
          finish: legFinish(leg),
          hex: leg.color,
          image: leg.image,
          dataAttr: "legId",
          onClick: () => commitPick(i),
        }),
      );
    }
  };

  let modal: ModalSheet | null = null;
  function openModal(): void {
    if (!modal) {
      modal = createModalSheet({ host: ctx.host, background: themeSheetBg(), onDismiss: () => {} });
    }
    modal.setTitle("All legs", String(list.length));
    modal.clearBody();
    const grid = el("div", "mob-modal-grid mob-modal-grid--3");
    for (let i = 0; i < list.length; i++) {
      const leg = list[i];
      const sel = ctx.state.get().legId === leg.id;
      grid.appendChild(
        modalSquareTile({
          label: leg.label,
          finish: legFinish(leg),
          hex: leg.color,
          selected: sel,
          onClick: () => {
            commitPick(i);
            modal?.close();
          },
        }),
      );
    }
    modal.body.appendChild(grid);
    modal.open();
  }

  function commitPick(newIdx: number): void {
    ctx.lru.leg = pushLru(ctx.lru.leg, newIdx, list.length);
    const leg = list[newIdx];
    if (!leg) return;
    // Smooth swap: onPick → state.set → sync() toggles is-selected on the existing
    // swatches (CSS transitions the halo/lift). Only rebuild if the pick wasn't on
    // screen (windowed >4 list).
    const onScreen = row.querySelector(`.mob-swatch[data-leg-id="${leg.id}"]`);
    ctx.onPick(leg.id);
    if (!onScreen) rebuildRow();
  }

  rebuildRow();
  tile.setSeeMoreVisible(list.length > 4);

  return {
    root: tile.root,
    sync() {
      const sel = ctx.state.get().legId;
      for (const btn of row.querySelectorAll<HTMLButtonElement>(".mob-swatch")) {
        btn.classList.toggle("is-selected", btn.dataset.legId === sel);
      }
    },
  };
}

/* ── Generic declared swatch row (config-driven, e.g. a lamp "Shade colour") ─── */

interface GenericRowCtx {
  state: Signal<ConfigState>;
  engine: Engine;
  host: HTMLElement;
}

/** A row declared entirely in the product config (no per-product code): renders the
 *  declared options as swatches; picking one recolours `row.target` (a named GLB
 *  part) to the option's colour. Selection is tracked locally (not in ConfigState). */
function buildGenericSwatchRow(row: SwatchRowDef, ctx: GenericRowCtx): TileApi {
  const tile = createGlassTile({ stepLabel: row.label, onSeeMore: () => openModal() });
  const rowEl = el("div", "mob-tile-row mob-tile-row--swatches");
  tile.body.appendChild(rowEl);
  // Top-right shows "see more" only when there are more options than the 4 inline.
  tile.setSeeMoreVisible(row.options.length > 4);
  let selectedId: string | null = null;

  function pick(opt: SwatchRowOption): void {
    selectedId = opt.id;
    ctx.engine.recolorPart(row.target, opt.color);
    for (const sw of rowEl.querySelectorAll<HTMLButtonElement>(".mob-swatch")) {
      sw.classList.toggle("is-selected", sw.dataset.optId === selectedId);
    }
    trackEvent("option_change", { row: row.id, to: opt.id });
  }

  for (const opt of row.options.slice(0, 4)) {
    rowEl.appendChild(
      squareSwatch({
        id: opt.id, label: opt.label, finish: "matte", hex: opt.color, image: opt.image,
        dataAttr: "optId", onClick: () => pick(opt),
      }),
    );
  }

  let modal: ModalSheet | null = null;
  function openModal(): void {
    if (row.options.length <= 4) return;
    if (!modal) {
      modal = createModalSheet({ host: ctx.host, background: themeSheetBg(), onDismiss: () => {} });
    }
    modal.setTitle(row.label, String(row.options.length));
    modal.clearBody();
    const grid = el("div", "mob-modal-grid mob-modal-grid--3");
    for (const opt of row.options) {
      grid.appendChild(
        modalSquareTile({
          label: opt.label, finish: "matte", hex: opt.color, selected: opt.id === selectedId,
          onClick: () => { pick(opt); modal?.close(); },
        }),
      );
    }
    modal.body.appendChild(grid);
    modal.open();
  }

  return {
    root: tile.root,
    sync() {}, // generic row tracks its own selection locally — nothing to re-derive
  };
}

/* ── GlassTile factory ───────────────────────────────────────── */

interface GlassTileOpts {
  stepLabel: string;
  onSeeMore?: () => void;
}

interface GlassTileApi {
  root: HTMLElement;
  body: HTMLElement;
  setSeeMoreVisible(visible: boolean): void;
  /** Brand-skin PDP pattern: the row header shows the SELECTED value inline
   *  ("Colour: Green Wattle"). No-op visually without a skin (CSS-gated). */
  setLabelValue(value: string): void;
}

function createGlassTile(opts: GlassTileOpts): GlassTileApi {
  const root = el("div", "mob-tile");
  const head = el("div", "mob-tile__head");
  // Brand skins drop our "0N ·" numbering and use the label:value header every
  // kit PDP shows (Koala "Colour: Green Wattle", Loaf/Castlery equivalents).
  const skinned = typeof document !== "undefined" && !!document.body?.dataset.nvSkin;
  const labelText = skinned
    ? opts.stepLabel.replace(/^\d+\s*·\s*/, "").replace(/^Colorway$/i, "Colour")
    : opts.stepLabel;
  head.appendChild(el("span", "mob-tile__label", labelText));
  const valueEl = el("span", "mob-tile__value");
  head.appendChild(valueEl);

  const right = el("span", "mob-tile__pick");
  head.appendChild(right);

  let seeMoreBtn: HTMLButtonElement | null = null;
  if (opts.onSeeMore) {
    seeMoreBtn = el("button", "mob-tile__seemore-btn");
    seeMoreBtn.type = "button";
    seeMoreBtn.innerHTML = "see more <span class=\"mob-tile__seemore-chev\">›</span>";
    seeMoreBtn.addEventListener("click", opts.onSeeMore);
  }

  const body = el("div", "mob-tile__body");
  root.append(head, body);

  // The header right slot is ONLY ever the "see more" button or EMPTY — never the
  // selected value's name (Alex 2026-06-01: top-right = "See more" when there are
  // genuinely more options than the row shows, otherwise nothing, never the name).
  // Each builder gates see-more to (option count > 4) via setSeeMoreVisible.
  let seeMore = !!opts.onSeeMore;

  function render(): void {
    right.textContent = "";
    if (seeMore && seeMoreBtn) {
      right.classList.add("mob-tile__pick--seemore");
      right.appendChild(seeMoreBtn);
    } else {
      right.classList.remove("mob-tile__pick--seemore");
    }
  }
  render();

  return {
    root,
    body,
    setSeeMoreVisible(visible: boolean) {
      if (!opts.onSeeMore) return;
      if (visible === seeMore) return;
      seeMore = visible;
      render();
    },
    setLabelValue(value: string) {
      valueEl.textContent = value || "";
    },
  };
}

/* ── square swatch (inline ~56×42 finish chip + label — ALL SQUARE) ── */

interface SquareSwatchOpts {
  id: string;
  label: string;
  finish: string;
  hex: string;
  /** Optional swatch thumbnail (uploaded texture image). When set it shows ON the
   *  chip instead of the flat finish+colour — set per option in the config. */
  image?: string;
  dataAttr: "materialId" | "colorwayId" | "legId" | "optId";
  onClick: () => void;
}

function squareSwatch(opts: SquareSwatchOpts): HTMLButtonElement {
  const btn = el("button", "mob-swatch");
  btn.type = "button";
  btn.dataset[opts.dataAttr] = opts.id;
  btn.setAttribute("aria-label", opts.label);

  const chip = el("span", "mob-swatch__chip");
  if (opts.image) {
    chip.style.backgroundImage = `url(${opts.image})`;
    chip.style.backgroundSize = "cover";
    chip.style.backgroundPosition = "center";
  } else {
    Object.assign(chip.style, materialBG(opts.finish, opts.hex));
  }
  btn.appendChild(chip);

  btn.appendChild(el("span", "mob-swatch__name", opts.label));
  btn.addEventListener("click", () => {
    if (btn.dataset.oos === "1") {
      showToast(btn.dataset.oosMsg || `${opts.label} is sold out right now.`);
      btn.animate(
        [{ transform: "translateX(0)" }, { transform: "translateX(-3px)" }, { transform: "translateX(3px)" }, { transform: "translateX(0)" }],
        { duration: 160, easing: "ease-in-out" },
      );
      return;
    }
    opts.onClick();
  });
  return btn;
}

interface ModalTileOpts {
  label: string;
  finish: string;
  hex: string;
  selected: boolean;
  oos?: boolean;
  /** Toast shown on tap when `oos`; default is the sold-out line. */
  oosMsg?: string;
  onClick: () => void;
}

function modalSquareTile(opts: ModalTileOpts): HTMLButtonElement {
  const btn = el("button", "mob-modal-tile" + (opts.selected ? " is-selected" : "") + (opts.oos ? " is-oos" : ""));
  btn.type = "button";
  if (opts.oos) btn.setAttribute("aria-disabled", "true");
  if (opts.oos && opts.oosMsg) btn.dataset.oosKind = "visual";
  const chip = el("span", "mob-modal-swatch");
  Object.assign(chip.style, materialBG(opts.finish, opts.hex));
  btn.appendChild(chip);
  btn.appendChild(el("span", "mob-modal-tile__name", opts.label));
  btn.addEventListener("click", () => {
    if (opts.oos) {
      showToast(opts.oosMsg || `${opts.label} is sold out right now.`);
      return;
    }
    opts.onClick();
  });
  return btn;
}

const reduceMotion = (): boolean =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Count-up/down the price number from one value to the next (ease-out, ~340ms). The
 *  cheapest single change with the biggest perceived-value lift: the price feels alive
 *  and responsive instead of hard-swapping. Gated behind prefers-reduced-motion. */
function animatePrice(el: HTMLElement, from: number, to: number, currency: string): void {
  if (reduceMotion() || from === to || !Number.isFinite(from)) {
    el.textContent = formatPrice(to, currency);
    return;
  }
  const start = performance.now();
  const dur = 340;
  const step = (now: number) => {
    const t = Math.min(1, (now - start) / dur);
    const eased = 1 - Math.pow(1 - t, 3);
    el.textContent = formatPrice(Math.round(from + (to - from) * eased), currency);
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/* ── PDP rail head ── the rail's opening: name, social proof, live price.
 * Composed like a real PDP title block (name → rating line → price + lead time
 * on a shared baseline), all from config data — nothing invented. */

/** BNPL badges (Alex 2026-06-23) — served locally from public/pay/ so the demo never
 *  depends on a third-party CDN. */
const PAY_ICONS: ReadonlyArray<readonly [string, string]> = [
  ["/pay/afterpay.png", "Afterpay"],
  ["/pay/zip.png", "Zip"],
];

/** "4 interest-free payments of €X.XX" — 2-decimal so the per-instalment amount is
 *  exact (formatPrice rounds to whole, which reads wrong for a quarter). */
function installSentence(total: number, currency: string): string {
  const quarter = total / 4;
  let amt: string;
  try {
    amt = new Intl.NumberFormat("en-US", {
      style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(quarter);
  } catch {
    amt = `${quarter.toFixed(2)} ${currency}`;
  }
  return `4 interest-free payments of ${amt}`;
}

function buildRailHead(product: ProductConfig): { root: HTMLElement; priceEl: HTMLElement; installEl: HTMLElement } {
  const root = el("div", "mob-rail-head");
  root.appendChild(el("h1", "mob-rail-head__name", product.productLabel));

  // PDP disposition (Alex 2026-06-23): title → rating → price, no description
  // paragraph. The rating sits DIRECTLY under the title (where a real sofa PDP
  // puts its stars + review-count link). Brands that deliberately park reviews
  // under the buy row (reviewsBelowBuy) keep them there and skip this head block.
  if (product.rating && !product.theme?.reviewsBelowBuy) {
    const meta = el("div", "mob-rail-head__meta");
    meta.appendChild(el("span", "mob-rail-head__stars", "★★★★★"));
    if (product.reviewCount) {
      meta.appendChild(el("span", "mob-rail-head__reviews", `${product.reviewCount} reviews`));
    }
    root.appendChild(meta);
  }

  const priceRow = el("div", "mob-rail-head__pricerow");
  const priceEl = el("span", "mob-rail-head__price", "");
  priceRow.appendChild(priceEl);
  if (product.leadTime) {
    priceRow.appendChild(el("span", "mob-rail-head__lead", product.leadTime));
  }
  root.appendChild(priceRow);

  // Buy-now-pay-later (Alex 2026-06-23): afterpay + zip badges, then a
  // "4 interest-free payments of €X" line — mirrors a real AU/UK sofa PDP. The
  // amount tracks the LIVE configured price (updated by the CTA sync via installEl).
  const pay = el("div", "mob-rail-head__pay");
  for (const [src, name] of PAY_ICONS) {
    const img = el("img", "mob-rail-head__pay-icon");
    img.src = src;
    img.alt = name;
    img.loading = "lazy";
    img.decoding = "async";
    pay.appendChild(img);
  }
  root.appendChild(pay);

  const installEl = el("p", "mob-rail-head__install", "");
  root.appendChild(installEl);

  return { root, priceEl, installEl };
}

/* ── CTA (sticky bottom) ─────────────────────────────────────── */

interface CtaApi {
  root: HTMLElement;
  sync(): void;
}

function buildCta(
  product: ProductConfig,
  state: Signal<ConfigState>,
  engine: Engine,
  opts?: { pdp?: boolean; headPriceEl?: HTMLElement; headInstallEl?: HTMLElement },
): CtaApi {
  const pdp = opts?.pdp ?? false;
  const headPriceEl = opts?.headPriceEl;
  const headInstallEl = opts?.headInstallEl;
  const wrap = el("div", "mob-cta-wrap");
  if (pdp) wrap.classList.add("is-pdp");
  // Embedded in a real product page: the configurator drives the merchant's OWN price
  // + Add-to-cart button (commerce.broadcastState), so its own buy bar is hidden — no
  // separate price block floating on the page. EXCEPT `?cta=self` (a plain website with
  // no native button to drive): keep our own buy bar + drawer as the buy.
  const params = new URLSearchParams(location.search);
  const embedded = params.has("embed");
  const ctaSelf = params.get("cta") === "self";
  let lastLivePrice: number | null = null; // for the price count-up animation
  let unsellable = false; // store resolved but a required variant is gone/not-found → block buy
  const btn = el("button", "mob-cta");
  btn.type = "button";
  btn.id = "mob-cta";

  const left = el("div", "mob-cta__left");
  // PDP mode: the product name lives in the rail head, so the bar is a plain "Add to cart".
  left.appendChild(el("span", "mob-cta__name", pdp ? "Add to cart" : product.productLabel));
  if (!pdp) {
    const subText = product.subDescriptor ?? product.description.split(".")[0];
    left.appendChild(el("span", "mob-cta__sub", subText));
  }

  const right = el("div", "mob-cta__right");
  const priceWrap = el("div", "mob-cta__price-wrap");
  const priceEl = el("span", "mob-cta__price", "");
  const installEl = el("span", "mob-cta__install", "");
  priceWrap.append(priceEl, installEl);
  const arrow = el("span", "mob-cta__arrow");
  arrow.textContent = "→";

  // Split buy row (Loaf): the price sits BESIDE the button, the button is a
  // plain labelled "Add to basket", and the arrow is dropped — matching their
  // real PDP. Otherwise the bar is the single pill (name + sub | price → arrow).
  const ctaSplit = !!product.theme?.ctaSplit;
  if (ctaSplit) {
    left.replaceChildren(el("span", "mob-cta__name", product.theme?.ctaLabel ?? "Add to basket"));
    btn.append(left);
    wrap.append(priceWrap, btn); // price first (CSS orders it left of the button)
    wrap.classList.add("mob-cta-wrap--split");
  } else {
    if (product.theme?.ctaLabel) left.replaceChildren(el("span", "mob-cta__name", product.theme.ctaLabel));
    right.append(priceWrap, arrow);
    btn.append(left, right);
    wrap.appendChild(btn);
  }
  if (embedded && !ctaSelf) wrap.style.display = "none";

  // Reviews under the buy row (Loaf): the rating moves out of the title block
  // to sit beneath price + Add to basket.
  if (product.theme?.reviewsBelowBuy && product.rating) {
    const r = el("div", "mob-buy-reviews");
    r.append(el("span", "mob-buy-reviews__star", "★"), el("span", undefined, `${product.rating.toFixed(1)}`));
    if (product.reviewCount) r.appendChild(el("span", "mob-buy-reviews__n", `${product.reviewCount} reviews`));
    wrap.appendChild(r);
  }

  // Trust badges under the CTA (Koala: 120-day trial · Free delivery · 5-year
  // warranty). Brand-true terms from the config, no invented numbers.
  const badges = product.theme?.trustBadges;
  if (badges && badges.length) {
    const row = el("div", "mob-cta__trust");
    for (const b of badges) row.appendChild(el("span", "mob-cta__trust-item", b));
    wrap.appendChild(row);
  }

  // Bloc theme: Caveat sub-line under the CTA (lead time copy).
  if (currentTheme() === "bloc" && product.leadTime) {
    wrap.appendChild(el("div", "mob-cta__caveat", product.leadTime));
  }

  btn.addEventListener("click", () => {
    const total = computePrice(product, state.get());
    trackEvent("add_to_cart", { productId: product.productId, ...state.get(), price: total });
    // Shopify-sourced: show the confirmation drawer with the configured snapshot, the
    // full piece list, and the live total; checkout from there does the real add-to-cart.
    if (shopifyActive(product)) {
      if (unsellable) {
        showToast("This combination isn't available to buy right now.");
        return;
      }
      if (configHasOOS(product, state.get(), liveAvail)) {
        showToast("One piece in this combination is sold out — try another colour or size.");
        return;
      }
      const snapshot = engine.captureSnapshot(900);
      openCartDrawer({
        snapshot,
        summary: configSummary(product, state.get()),
        lines: liveResult?.lines ?? [],
        total: liveResult?.total ?? null,
        currency: liveResult?.currency ?? product.currency,
        onCheckout: () => {
          const out = buyShopify(product, state.get());
          if (out.status === "blocked") showToast(out.message);
          else if (out.status === "navigating") closeCartDrawer();
        },
      });
      return;
    }
    // Made-to-order: the proven draft-order checkout path.
    const slug = currentSlug();
    if (!slug) {
      showToast(`${product.productLabel} added, ${formatPrice(total, product.currency)}`);
      return;
    }
    showToast("Opening checkout...");
    createCheckout(slug, state.get())
      .then((r) => goToCheckout(r.checkoutUrl))
      .catch((e) => showToast(e?.message || "Checkout unavailable, please try again"));
  });

  return {
    root: wrap,
    sync() {
      // Shopify-sourced: the STORE is the ONLY source of price. Never show the config's
      // computePrice — not even as a placeholder or a failure fallback. Show a loading state
      // until the live total arrives (keep the previous live price during a re-fetch), and
      // "—" only if the store is unreachable with no price yet.
      if (shopifyActive(product)) {
        // Defensive: if THIS exact combination has no sellable variant (e.g. a deep-linked or
        // otherwise-reached unmapped fabric), show it clearly and block the buy — NEVER keep a
        // stale price from the previous selection. The option-greying above normally prevents
        // ever landing here interactively; this is the backstop.
        const bom = resolveBOM(product, state.get());
        if (!bom.ok && bom.reason === "unmapped") {
          priceEl.textContent = "—";
          if (headPriceEl) headPriceEl.textContent = "Unavailable";
          if (headInstallEl) headInstallEl.textContent = "";
          installEl.textContent = "";
          lastLivePrice = null;
          unsellable = true;
          btn.classList.add("is-blocked");
          broadcastState(product, state.get(), null, false);
          return;
        }
        btn.classList.remove("is-blocked");
        if (lastLivePrice == null) {
          priceEl.textContent = "…";
          if (headPriceEl) headPriceEl.textContent = "…";
          if (headInstallEl) headInstallEl.textContent = "";
          installEl.textContent = "";
        }
        broadcastState(product, state.get(), null);
        hydrateLivePrice(currentSlug(), product, state.get(), (live) => {
          if (!live) {
            if (lastLivePrice == null) {
              priceEl.textContent = "—";
              if (headPriceEl) headPriceEl.textContent = "—";
              if (headInstallEl) headInstallEl.textContent = "";
              installEl.textContent = "";
            }
            return;
          }
          // The store resolved, but a line came back `found:false` (variant deleted, or
          // never created) → this configuration CANNOT be sold. Never render it as a real
          // $0 with a live buy; show Unavailable and block, exactly like an unmapped combo.
          const sellable = live.lines.length > 0 && live.lines.every((l) => l.found);
          if (!sellable) {
            priceEl.textContent = "—";
            if (headPriceEl) headPriceEl.textContent = "Unavailable";
            if (headInstallEl) headInstallEl.textContent = "";
            installEl.textContent = "";
            lastLivePrice = null;
            unsellable = true;
            liveResult = live;
            liveAvail = live.availability;
            btn.classList.add("is-blocked");
            broadcastState(product, state.get(), null, false);
            return;
          }
          unsellable = false;
          // Live total is in the STORE's currency. Count-up between live prices for a premium feel.
          if (lastLivePrice == null) priceEl.textContent = formatPrice(live.total, live.currency);
          else animatePrice(priceEl, lastLivePrice, live.total, live.currency);
          lastLivePrice = live.total;
          if (headPriceEl) headPriceEl.textContent = formatPrice(live.total, live.currency);
          if (headInstallEl) headInstallEl.textContent = installSentence(live.total, live.currency);
          installEl.textContent = `4× ${formatPrice(Math.round(live.total / 4), live.currency)}`;
          broadcastState(product, state.get(), live);
          // Out-of-stock: store the map, repaint the swatches, and block the buy bar if
          // the current combination contains a sold-out piece (backstop to the tile gating).
          liveResult = live;
          liveAvail = live.availability;
          repaintOOS?.();
          btn.classList.toggle("is-blocked", configHasOOS(product, state.get(), live.availability));
        });
        return;
      }
      // Made-to-order (no Shopify binding): the config price is the source of truth.
      const total = computePrice(product, state.get());
      priceEl.textContent = formatPrice(total, product.currency);
      if (headPriceEl) headPriceEl.textContent = priceEl.textContent;
      if (headInstallEl) headInstallEl.textContent = installSentence(total, product.currency);
      installEl.textContent = `4× ${formatPrice(Math.round(total / 4), product.currency)}`;
    },
  };
}

/* ── state mutations ─────────────────────────────────────────── */

/**
 * Pick a SHAPE (v50). INDEPENDENT axis: sets state.shapeId and re-resolves the
 * formeId from (newShape × currentSize). Does NOT touch sizeId — the current size
 * is preserved, so "2 Seater" → "3 Seater" while staying on Large stays Large.
 */
function selectShape(shapeId: string, product: ProductConfig, state: Signal<ConfigState>, engine: Engine): void {
  const prev = state.get();
  if (prev.shapeId === shapeId) return;
  const formeId = resolveForme(product, shapeId, prev.sizeId).id;
  trackEvent("shape_change", { from: prev.shapeId, to: shapeId, formeId });
  state.set({ ...prev, shapeId, formeId });
  // Roll the selection (tiles + price + URL) back if the build couldn't load, so
  // the UI never claims a shape the 3D didn't actually switch to (honest view).
  // "superseded" = a newer pick won — leave that pick's state alone.
  // Produit 360-first (`product.spin`) : le flipbook EST le visuel, le 3D cache ne sert
  // qu a l AR et aux cotes. Un GLB manquant ne doit pas annuler la selection (Alex
  // 2026-09-07). Sans `spin`, le rollback honnete du 3D reste tel quel.
  void engine.rebuildAssembly(product, state.get()).then((r) => {
    if (r === "failed" && !product.spin && state.get().formeId === formeId) state.set(prev);
  });
}

/**
 * Pick a SIZE (v50). INDEPENDENT axis: sets state.sizeId and re-resolves the
 * formeId from (currentShape × newSize). Does NOT touch shapeId — the current
 * shape is preserved, so Regular → Large keeps the same silhouette, just larger.
 */
function selectSize(sizeId: string, product: ProductConfig, state: Signal<ConfigState>, engine: Engine): void {
  const size = product.sizes?.find((z) => z.id === sizeId);
  if (!size) return;
  const prev = state.get();
  if (prev.sizeId === sizeId) return;
  const formeId = resolveForme(product, prev.shapeId, sizeId).id;
  trackEvent("size_change", { from: prev.sizeId, to: sizeId, formeId });
  state.set({ ...prev, sizeId, formeId });
  // Same honest rollback as selectShape if the build can't load.
  // Produit 360-first (`product.spin`) : le flipbook EST le visuel, le 3D cache ne sert
  // qu a l AR et aux cotes. Un GLB manquant ne doit pas annuler la selection (Alex
  // 2026-09-07). Sans `spin`, le rollback honnete du 3D reste tel quel.
  void engine.rebuildAssembly(product, state.get()).then((r) => {
    if (r === "failed" && !product.spin && state.get().formeId === formeId) state.set(prev);
  });
}

function selectLeg(id: string, product: ProductConfig, state: Signal<ConfigState>, engine: Engine): void {
  if (state.get().legId === id) return;
  trackEvent("leg_change", { from: state.get().legId, to: id });
  state.set({ ...state.get(), legId: id });
  const leg = product.legs.find((l) => l.id === id);
  if (leg) engine.applyLeg(leg);
}

/* ── DOM helpers ─────────────────────────────────────────────── */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

/** Zoom a photogrid frame to ITS OWN sofa so every shape/angle fills its square tile without
 *  ever clipping the sofa (Alex 2026-06-24). The 360 frames carry wide, uneven transparent
 *  margins — a corner spread is ~full width, a 1-seater is narrow — so one fixed CSS zoom can't
 *  fit them all. We measure each frame's opaque sofa bounds and set its individual transform:
 *  the sofa's LARGER side fills ~90% of the tile, centred on the sofa, never past the edge. */
function fitPhotogridImg(img: HTMLImageElement): void {
  try {
    if (!img.naturalWidth) return;
    const N = 100;
    const c = document.createElement("canvas");
    c.width = N; c.height = N;
    const cx = c.getContext("2d", { willReadFrequently: true });
    if (!cx) return;
    cx.drawImage(img, 0, 0, N, N);
    const d = cx.getImageData(0, 0, N, N).data;
    let minx = N, maxx = 0, miny = N, maxy = 0, found = false;
    for (let y = 0; y < N; y++) {
      for (let x = 0; x < N; x++) {
        if (d[(y * N + x) * 4 + 3] > 110) {        // solid sofa only, not the soft shadow halo
          found = true;
          if (x < minx) minx = x; if (x > maxx) maxx = x;
          if (y < miny) miny = y; if (y > maxy) maxy = y;
        }
      }
    }
    if (!found) { img.style.transform = ""; return; }
    const x0 = minx / N, x1 = (maxx + 1) / N, y0 = miny / N, y1 = (maxy + 1) / N;
    const TARGET = 0.9;                              // sofa's larger dimension fills 90% of the tile
    const scale = Math.min(2.4, TARGET / Math.max(x1 - x0, y1 - y0));
    // CENTRE the sofa's bbox in the tile, don't just scale in place: an L-shaped corner has an
    // OFF-centre bbox, so scaling around it shoved one arm against the border. translate+scale
    // from the top-left maps the bbox centre to the tile centre → 5% margin all round, every
    // shape, never touching the edge (Alex 2026-06-24).
    const tx = 0.5 - scale * ((x0 + x1) / 2);
    const ty = 0.5 - scale * ((y0 + y1) / 2);
    img.style.transformOrigin = "0 0";
    img.style.transform = `translate(${(tx * 100).toFixed(2)}%, ${(ty * 100).toFixed(2)}%) scale(${scale.toFixed(3)})`;
  } catch {
    img.style.transform = "";                       // tainted/unsupported → leave the plain contain fit
  }
}

/** Centered image lightbox for the neovaa demo photo grid. Close via the ✕ button, a
 *  click on the backdrop, or the Escape key. */
function openPhotoLightbox(src: string, alt: string): void {
  const overlay = el("div", "nv-lightbox");
  const fig = el("figure", "nv-lightbox__fig");
  const img = document.createElement("img");
  img.className = "nv-lightbox__img";
  img.src = src;
  img.alt = alt;
  fig.appendChild(img);
  const close = el("button", "nv-lightbox__close", "×");
  close.type = "button";
  close.setAttribute("aria-label", "Close");
  fig.appendChild(close);
  overlay.appendChild(fig);

  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") dispose(); };
  const dispose = () => {
    overlay.remove();
    document.removeEventListener("keydown", onKey);
  };
  overlay.addEventListener("click", (e) => { if (e.target === overlay) dispose(); });
  close.addEventListener("click", dispose);
  document.addEventListener("keydown", onKey);
  document.body.appendChild(overlay);
}

function menuItem(label: string, icon: SVGSVGElement, onClick: () => void): HTMLElement {
  const item = el("button", "mob-menu-item");
  item.type = "button";
  item.appendChild(icon);
  item.appendChild(el("span", undefined, label));
  item.addEventListener("click", onClick);
  return item;
}

/** Thin hairline between top-level menu sections (AR · Dimensions · View · Details). */
function menuSeparator(): HTMLElement {
  return el("div", "mob-menu-sep");
}

/** Concentric "target" glyph — the Details (parts) menu icon. */
function svgParts(): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "14");
  svg.setAttribute("height", "14");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.8");
  const outer = document.createElementNS(NS, "circle");
  outer.setAttribute("cx", "12"); outer.setAttribute("cy", "12"); outer.setAttribute("r", "9");
  outer.setAttribute("opacity", "0.5");
  const inner = document.createElementNS(NS, "circle");
  inner.setAttribute("cx", "12"); inner.setAttribute("cy", "12"); inner.setAttribute("r", "3");
  inner.setAttribute("fill", "currentColor"); inner.setAttribute("stroke", "none");
  svg.append(outer, inner);
  return svg;
}

/** Small filled dot — the per-part menu row glyph. */
function svgDot(): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "14");
  svg.setAttribute("height", "14");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "currentColor");
  const c = document.createElementNS(NS, "circle");
  c.setAttribute("cx", "12"); c.setAttribute("cy", "12"); c.setAttribute("r", "4");
  svg.appendChild(c);
  return svg;
}

/** Right chevron "›" — the Details nav affordance (pushes into the detail pane). */
function svgChevron(): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "14"); svg.setAttribute("height", "14");
  svg.setAttribute("viewBox", "0 0 24 24"); svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor"); svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round"); svg.setAttribute("stroke-linejoin", "round");
  svg.classList.add("mob-menu-chevron");
  const p = document.createElementNS(NS, "polyline");
  p.setAttribute("points", "9 6 15 12 9 18");
  svg.appendChild(p);
  return svg;
}

/** Left chevron "‹" — the back affordance (pops to the main pane). */
function svgChevronBack(): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "16"); svg.setAttribute("height", "16");
  svg.setAttribute("viewBox", "0 0 24 24"); svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor"); svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round"); svg.setAttribute("stroke-linejoin", "round");
  const p = document.createElementNS(NS, "polyline");
  p.setAttribute("points", "15 6 9 12 15 18");
  svg.appendChild(p);
  return svg;
}

/* ── SVG glyphs ──────────────────────────────────────────────── */

const NS = "http://www.w3.org/2000/svg";

/** Diagonal double-arrow "expand" glyph (↗↙) — the enter-fullscreen icon. */
function svgFullscreen(): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "13");
  svg.setAttribute("height", "13");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2.5");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  // Top-right arrowhead + bottom-left arrowhead + the diagonal shaft between them.
  for (const pts of ["14 4 20 4 20 10", "10 20 4 20 4 14"]) {
    const p = document.createElementNS(NS, "polyline");
    p.setAttribute("points", pts);
    svg.appendChild(p);
  }
  for (const [x1, y1, x2, y2] of [["20", "4", "14", "10"], ["4", "20", "10", "14"]]) {
    const line = document.createElementNS(NS, "line");
    line.setAttribute("x1", x1); line.setAttribute("y1", y1);
    line.setAttribute("x2", x2); line.setAttribute("y2", y2);
    svg.appendChild(line);
  }
  return svg;
}

/** Cross "X" glyph — the exit-fullscreen icon (swapped in while fullscreen). */
function svgClose(): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "13");
  svg.setAttribute("height", "13");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2.5");
  svg.setAttribute("stroke-linecap", "round");
  for (const [x1, y1, x2, y2] of [["5", "5", "19", "19"], ["19", "5", "5", "19"]]) {
    const line = document.createElementNS(NS, "line");
    line.setAttribute("x1", x1); line.setAttribute("y1", y1);
    line.setAttribute("x2", x2); line.setAttribute("y2", y2);
    svg.appendChild(line);
  }
  return svg;
}

function svgMenu(): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "12");
  svg.setAttribute("height", "12");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  for (const y of [6, 12, 18]) {
    const line = document.createElementNS(NS, "line");
    line.setAttribute("x1", "4");
    line.setAttribute("y1", String(y));
    line.setAttribute("x2", "20");
    line.setAttribute("y2", String(y));
    svg.appendChild(line);
  }
  return svg;
}

function svgAr(): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "14");
  svg.setAttribute("height", "14");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.6");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  for (const d of ["M2 8V5a3 3 0 013-3h3", "M16 2h3a3 3 0 013 3v3", "M22 16v3a3 3 0 01-3 3h-3", "M8 22H5a3 3 0 01-3-3v-3"]) {
    const p = document.createElementNS(NS, "path");
    p.setAttribute("d", d);
    svg.appendChild(p);
  }
  return svg;
}

function svgDims(): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "14");
  svg.setAttribute("height", "14");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.6");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  const rect = document.createElementNS(NS, "rect");
  rect.setAttribute("x", "2");
  rect.setAttribute("y", "8");
  rect.setAttribute("width", "20");
  rect.setAttribute("height", "8");
  rect.setAttribute("rx", "1");
  svg.appendChild(rect);
  for (const [x, y2] of [[6, 12], [10, 11], [14, 12], [18, 11]] as const) {
    const l = document.createElementNS(NS, "line");
    l.setAttribute("x1", String(x));
    l.setAttribute("y1", "8");
    l.setAttribute("x2", String(x));
    l.setAttribute("y2", String(y2));
    svg.appendChild(l);
  }
  return svg;
}

/** Eye glyph — the View menu icon (camera point-of-view), distinct from the
 *  dimensions ruler so the two menu rows never read alike. */
function svgEye(): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "14");
  svg.setAttribute("height", "14");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.6");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  const eye = document.createElementNS(NS, "path");
  eye.setAttribute("d", "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z");
  svg.appendChild(eye);
  const pupil = document.createElementNS(NS, "circle");
  pupil.setAttribute("cx", "12");
  pupil.setAttribute("cy", "12");
  pupil.setAttribute("r", "3");
  svg.appendChild(pupil);
  return svg;
}

// formeIcon kept available for fallback shape images if a forme provides none.
void formeIcon;
